import { demoProducts } from "@/data/demo-data"
import { demoContributors } from "@/data/demo-book-data"
import { demoTemplateCatalog } from "@/data/demo-template-data"
import { demoPrintShops } from "@/data/demo-print-shop-data"
import { printShopSchema, printShopToInput } from "@/lib/print-shops"
import { demoWorkspaceUsers } from "@/data/demo-user-data"
import { demoCatalogCollections, demoCatalogItemTypes, demoCatalogProducts, demoKitPresets, demoPipelineCatalog, demoProductionRuns } from "@/data/demo-catalog-data"
import { DomainError, type EditorialRepository } from "@/data/repository"
import { isBarcaWorkspaceEmail } from "@/lib/auth"
import { normalizeTextSearch } from "@/lib/text"
import { imprintLogoFileError } from "@/lib/imprint-logos"
import { normalizeDriveFolderUrl, normalizeWebsiteUrl } from "@/lib/url"
import { addWorkingDays, isIsoDate, runStageProgress, scheduleStages, stageEstimateDays, todayIsoDate } from "@/lib/production-schedule"
import { buildProductFolderScaffold, validateScaffoldInput } from "../../supabase/functions/_shared/drive-folder-scaffold"
import { parseCreditsParagraphs } from "../../supabase/functions/_shared/credits-extraction"
import { extractedFaoFields, parseFaoParagraphs } from "../../supabase/functions/_shared/fao-extraction"
import { extractDriveFileId, supplementaryFileName, extractedCatalogRecordFields, hasSupplementaryFileExtension, parseCatalogRecordText, SUPPLEMENTARY_FILES_FOLDER_NAME } from "../../supabase/functions/_shared/catalog-record-extraction"
import type {
  AssignableUser,
  MyTask,
  SetProductionLeadInput,
  SetProductionStageAssignmentInput,
  SetUserTeamInput,
  UpdateUserInput,
  EditorialFunction,
  BlockItemInput,
  CatalogFilters,
  CatalogCollectionInput,
  CatalogCollectionSummary,
  CatalogItem,
  CatalogItemActivity,
  CatalogItemDocument,
  CatalogItemMockupAsset,
  CatalogItemEditionSnapshot,
  CatalogItemInput,
  CatalogPage,
  CatalogPagination,
  CatalogProduct,
  CatalogProductSummary,
  CatalogPublicMediaSelection,
  CatalogImageAltText,
  SaveCatalogPublicMediaOptions,
  CatalogSearchResult,
  CatalogSort,
  CreateKitTemplateInput,
  CreatePipelineTemplateInput,
  CreateCatalogProductInput,
  CreateProductDriveFoldersInput,
  DashboardData,
  DeleteCatalogItemDocumentInput,
  DeleteCatalogItemDocumentResult,
  DeleteCatalogItemMockupAssetInput,
  DeleteCatalogItemMockupAssetResult,
  DeleteCatalogProductResult,
  ImprintInput,
  ImprintKind,
  ImprintLogo,
  ImprintLogoTarget,
  ImprintSummary,
  InviteUserInput,
  ItemTypeInput,
  ItemTypeVariantInput,
  ProductDetail,
  ProductSummary,
  KitTemplateItemInput,
  PipelineVersionContentInput,
  PrintShopInput,
  PrintShopSummary,
  TransitionItemInput,
  SkipProductionItemInput,
  ReturnProductionItemInput,
  SaveCatalogProductInput,
  StartProductionInput,
  UpdateProductionInput,
  UploadCatalogItemDocumentInput,
  UploadCatalogItemMockupAssetInput,
  UploadCatalogProductImageInput,
  UploadToDriveStageInput,
  DriveItemStageFolders,
  ProductDriveFoldersResult,
  DriveStageFolder,
  DriveSyncItem,
  DriveSyncResult,
  DriveUploadedFile,
  GoogleDriveConnection,
  SyncProductionWithDriveInput,
  WorkspaceUser,
  UploadImprintLogoInput,
  ProductionPrintDelivery,
  ProductionPrintItem,
  CatalogRecordExtraction,
  UploadSupplementaryFileInput,
  SupplementaryFileKind,
  CreditsExtraction,
  FaoExtraction,
} from "@/types/domain"
import type { PrintChecklistKey } from "@/lib/print-checklist"
import { summarizePrintDeliveries } from "@/lib/print-deliveries"

const demoCreditsFileParagraphs = [
  { text: "Ficha de créditos", bold: true }, { text: "LIVRO", bold: true }, { text: "", bold: false },
  ...[["Tradução", "Gabriel Schaf"], ["Edição e Notas", "Marina Rocha"], ["Revisão", "Caio Nunes"]].flatMap(([role, name]) => [{ text: role, bold: false }, { text: name, bold: false }, { text: "", bold: false }]),
  { text: "Barca", bold: true },
  ...[["Direção", "Equipe Barca"], ["Diagramação", "Lia Martins"]].flatMap(([role, name]) => [{ text: role, bold: false }, { text: name, bold: false }, { text: "", bold: false }]),
]

const demoFaoFileParagraphs = [
  { text: "Ficha de acompanhamento da obra (FAO)", bold: true },
  { text: "I) Informações gerais", bold: true }, { text: "Título original: On Liberty", bold: false },
  { text: "II) Informações de contexto, conteúdo e forma da obra", bold: true },
  { text: "Sinopse:", bold: true }, { text: "Ensaio sobre os limites do poder que a sociedade pode exercer legitimamente sobre o indivíduo.", bold: false },
  { text: "Contexto:", bold: true }, { text: "Publicado em 1859, em meio aos debates sobre reformas políticas na Inglaterra vitoriana.", bold: false },
  { text: "Temas principais", bold: true }, ...["Liberdade de pensamento e de expressão", "O princípio do dano", "Individualidade e progresso"].map((text) => ({ text, bold: false, list: true })),
  { text: "III) Sobre o autor", bold: true },
  { text: "Breve biografia:", bold: true }, { text: "John Stuart Mill (1806–1873), filósofo e economista inglês.", bold: false },
  { text: "Principais obras:", bold: true }, ...["Sistema de lógica (1843)", "O utilitarismo (1861)"].map((text) => ({ text, bold: false, list: true })),
  { text: "A edição", bold: true }, { text: "Diferenciais", bold: true }, { text: "Tradução direta do inglês, com notas.", bold: false, list: true },
  { text: "Livreto", bold: true }, { text: "Conteúdo do livreto do box, ignorado na leitura.", bold: false },
]

const demoCatalogRecordFileText = `Dados Internacionais de Catalogação na Publicação (CIP)

M645u Mill, John Stuart
O utilitarismo / John Stuart Mill ; tradução de Gabriel Schaf. – 1. ed. – Porto Alegre : BARCA Editorial, 2026.

224 p.

ISBN 978-65-00000-00-0

1. Filosofia moral. 2. Ética. 3. Utilitarismo. I. Título.
CDU 17
CDD 171.5
Bibliotecária responsável: Equipe Barca – CRB 10/0000

Índice para catálogo sistemático
Ética: 171.5`

const wait = (ms = 140) => new Promise((resolve) => setTimeout(resolve, ms))
// Versões de edição dos registros de configuração que não guardam versão nos dados de demonstração.
const demoEditVersions = new Map<string, number>()
const demoEditVersion = (id: string) => demoEditVersions.get(id) ?? 1
function bumpDemoEditVersion(id: string, expectedVersion?: number) {
  if (expectedVersion !== undefined && expectedVersion !== demoEditVersion(id)) throw new DomainError("CONCURRENCY_CONFLICT", "Este registro foi alterado por outra pessoa.")
  demoEditVersions.set(id, demoEditVersion(id) + 1)
}
const demoDocumentFiles = new Map<string, File>()
const demoItemMockupAssetFiles = new Map<string, File>()
const demoProductImageFiles = new Map<string, File>()
const demoImprintLogos = new Map<string, ImprintLogo[]>()
const demoPipelineStageEstimates = new Map<string, import("@/types/domain").PipelineStageEstimate[]>([
  ["pipeline-filosofia", [
    { imprintId: "imprint-filosofia", stageName: "Briefing", days: 1 },
    { imprintId: "imprint-filosofia", stageName: "Editorial", days: 3 },
    { imprintId: "imprint-filosofia", stageName: "Design", days: 4 },
    { imprintId: "imprint-literatura", stageName: "Editorial", days: 2.5 },
  ]],
])
const demoCatalogItemActivities = new Map<string, CatalogItemActivity[]>()
const demoCatalogItemEditionSnapshots = new Map<string, CatalogItemEditionSnapshot>()
const demoCatalogActor = { id: "demo-ops", name: "Marina Rocha" }
/** Data de lançamento do payload; `undefined` quando o payload não informa, `null` quando limpa. */
function demoImprintLaunch(input: ImprintInput) {
  if (input.launchMonth === undefined && input.launchYear === undefined) return undefined
  const launchMonth = input.launchMonth ?? null
  const launchYear = input.launchYear ?? null
  if ((launchMonth === null) !== (launchYear === null) || (launchMonth !== null && (!Number.isInteger(launchMonth) || launchMonth < 1 || launchMonth > 12)) || (launchYear !== null && (!Number.isInteger(launchYear) || launchYear < 1900 || launchYear > 2100))) {
    throw new DomainError("INVALID_IMPRINT_LAUNCH", "Informe mês e ano de lançamento válidos.")
  }
  return launchMonth === null || launchYear === null ? null : { launchMonth, launchYear }
}

async function fileSha256(file: File) {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer())
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("")
}

function normalizedDocumentMime(file: File) {
  if (file.type && file.type !== "application/octet-stream") {
    if (file.type === "image/jpg") return "image/jpeg"
    if (file.type === "text/x-markdown") return "text/markdown"
    return file.type
  }
  const extension = file.name.split(".").pop()?.toLowerCase()
  const byExtension: Record<string, string> = {
    pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    md: "text/markdown",
    markdown: "text/markdown",
    txt: "text/plain",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
  }
  return byExtension[extension ?? ""] ?? "application/octet-stream"
}

function summary(product: ProductDetail): ProductSummary {
  const stageDistribution: Record<string, number> = {}
  let completedItemCount = 0
  let blockedItemCount = 0
  let criticalAgingDays = 0

  for (const item of product.items) {
    stageDistribution[item.currentStageId] = (stageDistribution[item.currentStageId] ?? 0) + 1
    if (item.status === "DONE") completedItemCount += 1
    if (item.status === "BLOCKED") blockedItemCount += 1
    const aging = Math.max(0, Math.floor((Date.now() - new Date(item.enteredAt).getTime()) / 86_400_000))
    criticalAgingDays = Math.max(criticalAgingDays, aging)
  }

  const allItemsDone = product.items.length > 0 && product.items.every((item) => item.status === "DONE")
  const allGatesDone = product.globalGates.every((gate) => gate.status === "DONE")
  const status = product.status === "DRAFT" || product.status === "ARCHIVED"
    ? product.status
    : blockedItemCount > 0
      ? "BLOCKED"
      : allItemsDone && allGatesDone
        ? "COMPLETED"
        : "ACTIVE"

  return {
    id: product.id,
    code: product.code,
    name: product.name,
    referenceMonth: product.referenceMonth,
    imprint: product.imprint,
    owner: product.owner,
    status,
    itemCount: product.items.length || product.itemCount,
    completedItemCount,
    criticalAgingDays,
    blockedItemCount,
    stages: product.stages,
    stageDistribution,
  }
}

function itemType(itemTypeId: string) {
  const definition = demoTemplateCatalog.itemTypes.find((candidate) => candidate.id === itemTypeId || candidate.code === itemTypeId)
  if (!definition) throw new DomainError("VALIDATION_ERROR", "Tipo de item inválido.")
  return definition
}

function kitItems(items: KitTemplateItemInput[]) {
  if (!items.length || items.some((item) => !item.variantId || !item.defaultName.trim())) {
    throw new DomainError("VALIDATION_ERROR", "O kit precisa ter itens válidos.")
  }
  return items.map((item, index) => {
    const type = itemType(item.itemTypeId)
    const variant = demoCatalogItemTypes.find((candidate) => candidate.id === type.id)?.variants
      .find((candidate) => candidate.id === item.variantId)
    if (!variant) throw new DomainError("INVALID_ITEM_VARIANT", "A variante selecionada não pertence ao tipo do item.")
    return {
      id: `kit-item-${crypto.randomUUID()}`,
      itemTypeId: type.id,
      itemTypeCode: type.code,
      itemTypeName: type.name,
      variantId: variant.id,
      variantName: variant.name,
      defaultName: item.defaultName.trim(),
      position: index + 1,
    }
  })
}

function pipelineContent(input: PipelineVersionContentInput) {
  if (!input.stages.length) throw new DomainError("VALIDATION_ERROR", "O fluxo de trabalho precisa ter ao menos uma etapa.")
  const stages = input.stages.map((stage, stageIndex) => {
    if (!stage.name.trim() || !stage.shortName.trim() || !stage.applicableItemTypeIds.length) {
      throw new DomainError("VALIDATION_ERROR", "Preencha os dados obrigatórios de todas as etapas.")
    }
    stage.applicableItemTypeIds.forEach(itemType)
    return {
      id: `pipeline-stage-${crypto.randomUUID()}`,
      name: stage.name.trim(),
      shortName: stage.shortName.trim(),
      position: stageIndex + 1,
      editorialFunction: stage.editorialFunction ?? null,
      usesPreviousDriveFolder: stage.usesPreviousDriveFolder ?? false,
      applicableItemTypeIds: [...new Set(stage.applicableItemTypeIds)],
      checklists: stage.checklists.map((check, checkIndex) => {
        if (!check.label.trim()) throw new DomainError("VALIDATION_ERROR", "Todo controle precisa de um rótulo.")
        if (check.itemTypeIds.some((itemTypeId) => !stage.applicableItemTypeIds.includes(itemTypeId))) {
          throw new DomainError("VALIDATION_ERROR", "O tipo do controle deve ser aplicável à etapa.")
        }
        return { id: `pipeline-check-${crypto.randomUUID()}`, label: check.label.trim(), required: false, itemTypeIds: [...new Set(check.itemTypeIds)], position: checkIndex + 1 }
      }),
    }
  })
  const gates = input.gates.map((gate, index) => {
    if (!gate.label.trim()) throw new DomainError("VALIDATION_ERROR", "Todo item do checklist global precisa de um rótulo.")
    return { id: `pipeline-gate-${crypto.randomUUID()}`, label: gate.label.trim(), required: false, position: index + 1 }
  })
  return { stages, gates }
}

function findPipelineVersion(versionId: string) {
  for (const template of demoTemplateCatalog.pipelineTemplates) {
    const version = template.versions.find((candidate) => candidate.id === versionId)
    if (version) return { template, version }
  }
  throw new DomainError("NOT_FOUND", "Versão de fluxo de trabalho não encontrada.")
}

function catalogSummary(product: CatalogProduct): CatalogProductSummary {
  const mainBook = product.items.find((item) => item.itemType.isBook)
  const mainBookAuthor = mainBook?.book?.author
    ?? mainBook?.book?.credits.find((credit) => credit.roleCode === "AUTHOR")?.contributor?.name
  return {
    id: product.id, sku: product.sku, name: product.name, author: product.author, description: product.description, status: product.status,
    version: product.version, imprint: product.imprint, collection: product.collection, publicationMonth: product.publicationMonth,
    publicationYear: product.publicationYear, salesModel: product.salesModel,
    mainBook: mainBook ? { title: mainBook.name, author: mainBookAuthor } : undefined, itemCount: product.items.length,
    productionCount: product.productionCount, currentRunId: product.currentRunId, updatedAt: product.updatedAt,
    publicSlug: product.publicSlug, publicPublishedAt: product.publicPublishedAt,
  }
}

function compareCatalogProducts(left: CatalogProduct, right: CatalogProduct, sort: CatalogSort = "UPDATED_DESC") {
  if (sort === "UPDATED_DESC") {
    return right.updatedAt.localeCompare(left.updatedAt) || right.id.localeCompare(left.id)
  }
  const leftPublication = left.publicationYear && left.publicationMonth
    ? left.publicationYear * 100 + left.publicationMonth
    : undefined
  const rightPublication = right.publicationYear && right.publicationMonth
    ? right.publicationYear * 100 + right.publicationMonth
    : undefined
  if (leftPublication === undefined || rightPublication === undefined) {
    if (leftPublication === rightPublication) return sort === "PUBLICATION_ASC" ? left.id.localeCompare(right.id) : right.id.localeCompare(left.id)
    return leftPublication === undefined ? 1 : -1
  }
  const publicationOrder = sort === "PUBLICATION_ASC"
    ? leftPublication - rightPublication
    : rightPublication - leftPublication
  return publicationOrder || (sort === "PUBLICATION_ASC" ? left.id.localeCompare(right.id) : right.id.localeCompare(left.id))
}

function paginateCatalog<T>(items: T[], totalCount: number, filteredCount: number, pagination: CatalogPagination): CatalogPage<T> {
  const requestedOffset = Number.parseInt(pagination.cursor ?? "0", 10)
  const offset = Number.isFinite(requestedOffset) && requestedOffset > 0 ? requestedOffset : 0
  const limit = Math.min(Math.max(pagination.limit ?? 50, 1), 100)
  const pageItems = items.slice(offset, offset + limit)
  const nextOffset = offset + pageItems.length
  return {
    items: structuredClone(pageItems),
    nextCursor: nextOffset < items.length ? String(nextOffset) : undefined,
    totalCount,
    filteredCount,
  }
}

function findCatalogProduct(productId: string) {
  const product = demoCatalogProducts.find((candidate) => candidate.id === productId)
  if (!product) throw new DomainError("NOT_FOUND", "Produto de catálogo não encontrado.")
  return product
}

function findCatalogItem(itemId: string) {
  for (const product of demoCatalogProducts) {
    const item = product.items.find((candidate) => candidate.id === itemId)
    if (item) return { product, item }
  }
  throw new DomainError("NOT_FOUND", "Item de catálogo não encontrado.")
}

function catalogItemSnapshotData(item: CatalogItem): CatalogItemEditionSnapshot["snapshotData"] {
  const snapshot = structuredClone(item) as Partial<CatalogItem>
  delete snapshot.version
  delete snapshot.currentEditionSequence
  return snapshot as CatalogItemEditionSnapshot["snapshotData"]
}

function recordCatalogItemActivity(itemId: string, activity: Omit<CatalogItemActivity, "id" | "actor" | "createdAt"> & { createdAt?: string }) {
  const current = demoCatalogItemActivities.get(itemId) ?? []
  const { createdAt, ...entry } = activity
  current.unshift({ id: `catalog-event-${crypto.randomUUID()}`, actor: demoCatalogActor, createdAt: createdAt ?? new Date().toISOString(), ...entry })
  demoCatalogItemActivities.set(itemId, current)
}

function validateCatalogProductInput(input: SaveCatalogProductInput) {
  const invalidMonth = input.publicationMonth !== undefined && (!Number.isInteger(input.publicationMonth) || input.publicationMonth < 1 || input.publicationMonth > 12)
  const invalidYear = input.publicationYear !== undefined && (!Number.isInteger(input.publicationYear) || input.publicationYear < 1000 || input.publicationYear > 3000)
  if (!input.name.trim() || !input.imprintId || invalidMonth || invalidYear) throw new DomainError("VALIDATION_ERROR", "Revise os dados gerais do produto.")
  if (input.items.some((item) => !item.name.trim() || !item.itemTypeId)) throw new DomainError("VALIDATION_ERROR", "Todos os itens precisam de nome e tipo.")
  if (input.items.some((item) => item.variantId && !demoCatalogItemTypes.find((type) => type.id === item.itemTypeId)?.variants.some((variant) => variant.id === item.variantId))) {
    throw new DomainError("INVALID_ITEM_VARIANT", "A variante selecionada não pertence ao tipo do item.")
  }
  if (input.collectionId && !demoCatalogCollections.some((collection) => collection.id === input.collectionId && collection.imprint.id === input.imprintId)) {
    throw new DomainError("INVALID_CATALOG_COLLECTION", "Selecione uma coleção do mesmo selo do produto.")
  }
  if (input.driveFolderUrl?.trim() && !normalizeDriveFolderUrl(input.driveFolderUrl)) {
    throw new DomainError("INVALID_DRIVE_FOLDER_URL", "Informe um link válido de pasta do Google Drive.")
  }
}

function validateCatalogProductCreation(input: CreateCatalogProductInput) {
  validateCatalogProductInput(input)
  if (!input.salesModel) throw new DomainError("VALIDATION_ERROR", "Selecione a modalidade de venda.")
}

function catalogItemFromInput(productId: string, input: CatalogItemInput, position: number, current?: CatalogItem): CatalogItem {
  const type = demoCatalogItemTypes.find((candidate) => candidate.id === input.itemTypeId)
  if (!type) throw new DomainError("VALIDATION_ERROR", "Tipo de item inválido.")
  const contributors = input.book?.credits.map((credit, index) => {
    if (credit.roleCode === "SECTION") return { id: `catalog-credit-${crypto.randomUUID()}`, contributor: null, roleCode: credit.roleCode, roleName: credit.displayLabel || "Seção", displayLabel: credit.displayLabel || "Seção", position: index + 1 }
    let contributor = demoContributors.find((candidate) => candidate.id === credit.contributorId)
    if (!contributor) {
      contributor = { id: credit.contributorId ?? `contributor-${crypto.randomUUID()}`, name: credit.contributorName.trim(), nationality: credit.nationality, birthYear: credit.birthYear, deathYear: credit.deathYear }
      demoContributors.push(contributor)
    }
    return { id: `catalog-credit-${crypto.randomUUID()}`, contributor, roleCode: credit.roleCode, roleName: credit.displayLabel || credit.roleCode, displayLabel: credit.displayLabel || credit.roleCode, position: index + 1 }
  }) ?? []
  return {
    id: current?.id ?? input.id ?? `catalog-item-${crypto.randomUUID()}`, productId, name: input.name.trim(), position,
    sku: input.sku?.trim() || undefined,
    printShop: input.printShopId ? structuredClone(demoPrintShops.find((shop) => shop.id === input.printShopId)) : undefined,
    printSpecifications: input.printSpecifications?.trim() || undefined,
    printFilesUrl: input.printFilesUrl?.trim() || undefined,
    currentReprint: input.currentReprint?.trim() || undefined,
    version: (current?.version ?? 0) + 1, currentEditionSequence: current?.currentEditionSequence ?? 1,
    itemType: { id: type.id, code: type.code, name: type.name, isBook: type.isBook },
    variant: type.variants.find((candidate) => candidate.id === input.variantId),
    technicalValues: type.fields.map((field) => ({ fieldId: field.id, code: field.code, label: field.label, type: field.type, unit: field.unit, value: input.technicalValues.find((value) => value.fieldId === field.id)?.value ?? null })),
    book: type.isBook ? { ...input.book, technicalExtras: input.book?.technicalExtras ?? [], credits: contributors } : undefined,
    mockupAssets: current?.mockupAssets ?? [],
    documents: current?.documents ?? [],
  }
}

function productionSummary(run: import("@/types/domain").ProductionRun): import("@/types/domain").ProductionRunSummary {
  const activeItems = run.items.filter((item) => item.status !== "CANCELLED")
  const stageStates = activeItems.flatMap((item) => item.stageStates)
  const activeStates = stageStates.filter((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED")
  const latestChangeAt = run.changes.reduce<string | undefined>((latest, change) => !latest || change.createdAt > latest ? change.createdAt : latest, undefined)
  return {
    id: run.id, sequence: run.sequence, referenceMonth: run.referenceMonth, kitQuantity: run.kitQuantity, isReprint: run.isReprint ?? false,
    status: run.status, version: run.version, startedAt: run.startedAt, updatedAt: latestChangeAt ?? run.completedAt ?? run.startedAt, completedAt: run.completedAt,
    product: { id: run.product.id, sku: run.product.sku, name: run.product.name, imprint: run.product.imprint },
    pipeline: { id: run.pipeline.id, name: run.pipeline.name, version: run.pipeline.version },
    itemCount: activeItems.length,
    completedItemCount: run.items.filter((item) => item.status === "COMPLETED").length,
    stageCount: stageStates.length,
    completedStageCount: stageStates.filter((state) => state.status === "DONE" || state.status === "SKIPPED").length,
    blockedItemCount: run.items.filter((item) => item.status === "BLOCKED").length,
    criticalAgingDays: Math.max(0, ...activeStates.map((state) => state.enteredAt ? Math.floor((Date.now() - new Date(state.enteredAt).getTime()) / 86_400_000) : 0)),
    currentStages: [...run.stages]
      .filter((stage) => runStageProgress(run, stage.id).current)
      .sort((first, second) => first.position - second.position)
      .map((stage) => ({ name: stage.name, position: stage.position })),
    pipelineStageCount: run.stages.length,
    targetEndDate: run.targetEndDate ?? null,
    openStageDueDate: demoApplicableStages(run)
      .filter((stage) => stage.plannedEndDate && !runStageProgress(run, stage.id).done)
      .map((stage) => stage.plannedEndDate!)
      .sort()[0] ?? null,
  }
}

function findProductionRun(runId: string) {
  const run = demoProductionRuns.find((candidate) => candidate.id === runId)
  if (!run) throw new DomainError("NOT_FOUND", "Produção não encontrada.")
  return run
}

function latestDemoRunForProduct(productId: string) {
  return demoProductionRuns
    .filter((candidate) => candidate.product.id === productId)
    .sort((first, second) => second.sequence - first.sequence)[0]
}

function assertActiveDemoRun(run: import("@/types/domain").ProductionRun) {
  if (run.status !== "ACTIVE") throw new DomainError("ACTIVE_PRODUCTION_REQUIRED", "A produção precisa estar ativa para receber operações.")
}

function recalculateDemoRun(run: import("@/types/domain").ProductionRun) {
  if (run.status !== "ACTIVE") return
  const openItems = run.items.filter((item) => item.status !== "CANCELLED")
  if (openItems.length && openItems.every((item) => item.status === "COMPLETED") && run.gates.filter((gate) => gate.required).every((gate) => gate.status === "DONE")) {
    const timestamp = new Date().toISOString()
    const product = findCatalogProduct(run.product.id)
    run.status = "COMPLETED"; run.completedAt = timestamp; run.product.status = "READY"
    product.status = "READY"; product.currentRunId = undefined; product.version += 1; product.updatedAt = timestamp
  }
  run.version += 1
}

/** Refaz o cronograma das etapas aplicáveis a partir da data final prevista, com os ajustes e as estimativas atuais do selo. */
function demoApplicableStages(run: import("@/types/domain").ProductionRun) {
  const applicableStageIds = new Set(run.items.filter((item) => item.status !== "CANCELLED").flatMap((item) => item.stageStates.map((state) => state.stageId)))
  return run.stages.filter((stage) => applicableStageIds.has(stage.id)).sort((first, second) => first.position - second.position)
}

function scheduleDemoRun(run: import("@/types/domain").ProductionRun) {
  const applicable = demoApplicableStages(run)
  const applicableStageIds = new Set(applicable.map((stage) => stage.id))
  const imprintId = findCatalogProduct(run.product.id).imprint.id
  const estimates = new Map(stageEstimateDays(demoPipelineStageEstimates.get(run.pipeline.templateId) ?? [], imprintId, applicable).map((entry) => [entry.id, entry.days]))
  // Etapas concluídas mantêm as datas e ficam fora da conta, como no banco.
  const done = new Set(applicable.filter((stage) => runStageProgress(run, stage.id).done).map((stage) => stage.id))
  const schedule = new Map(run.targetEndDate
    ? scheduleStages(applicable.filter((stage) => !done.has(stage.id)).map((stage) => ({ id: stage.id, days: stage.manualDays ?? estimates.get(stage.id) ?? null })), run.targetEndDate).map((entry) => [entry.stageId, entry])
    : [])
  run.stages = run.stages.map((stage) => {
    const isApplicable = applicableStageIds.has(stage.id) && Boolean(run.targetEndDate)
    const entry = done.has(stage.id) ? { plannedStartDate: stage.plannedStartDate, plannedEndDate: stage.plannedEndDate } : schedule.get(stage.id)
    return { ...stage, estimatedDays: isApplicable ? estimates.get(stage.id) ?? null : null, manualDays: isApplicable ? stage.manualDays ?? null : null, plannedStartDate: isApplicable ? entry?.plannedStartDate ?? null : null, plannedEndDate: isApplicable ? entry?.plannedEndDate ?? null : null }
  })
}

// No modo demonstração, a produção de exemplo termina daqui a cinco dias úteis:
// a primeira etapa fica atrasada e as seguintes caem nesta semana e na próxima.
for (const run of demoProductionRuns) {
  if (run.status !== "ACTIVE" || run.targetEndDate) continue
  run.targetEndDate = addWorkingDays(todayIsoDate(), 5)
  scheduleDemoRun(run)
}

function demoAssignableUser(userId: string, code: "INVALID_ASSIGNEE" | "INVALID_PRODUCTION_LEAD" = "INVALID_ASSIGNEE") {
  const user = demoWorkspaceUsers.find((candidate) => candidate.id === userId)
  if (!user || (user.role !== "ADMIN" && user.role !== "OPS")) throw new DomainError(code, code === "INVALID_ASSIGNEE" ? "Somente administradores e operadores podem ser responsáveis por etapas." : "O responsável geral precisa ser um administrador ou operador.")
  return user
}

function demoTaskFor(run: import("@/types/domain").ProductionRun, assignment: import("@/types/domain").ProductionStageAssignment): MyTask | undefined {
  const stage = run.stages.find((candidate) => candidate.id === assignment.stageId)
  if (!stage) return undefined
  const states = run.items.filter((item) => item.status !== "CANCELLED").flatMap((item) => item.stageStates.filter((state) => state.stageId === stage.id))
  const inProgress = states.filter((state) => state.status === "IN_PROGRESS")
  const blocked = states.filter((state) => state.status === "BLOCKED")
  const done = states.filter((state) => state.status === "DONE" || state.status === "SKIPPED")
  const readySince = [...inProgress, ...blocked].map((state) => state.enteredAt).filter((value): value is string => Boolean(value)).sort()[0] ?? null
  const status: MyTask["status"] = run.status === "ACTIVE" && inProgress.length + blocked.length > 0 ? "READY" : states.length > 0 && done.length === states.length ? "DONE" : "WAITING"
  return {
    id: `${run.id}:${stage.id}`, status, assignedAt: assignment.assignedAt, readySince,
    itemCount: states.length, inProgressItemCount: inProgress.length, blockedItemCount: blocked.length, completedItemCount: done.length,
    plannedStartDate: stage.plannedStartDate ?? null, plannedEndDate: stage.plannedEndDate ?? null, estimatedDays: stage.estimatedDays ?? null, manualDays: stage.manualDays ?? null,
    stage: { id: stage.id, name: stage.name, shortName: stage.shortName, position: stage.position }, stageCount: run.stages.length,
    run: { id: run.id, sequence: run.sequence, referenceMonth: run.referenceMonth, status: run.status, targetEndDate: run.targetEndDate ?? null },
    product: { id: run.product.id, sku: run.product.sku, name: run.product.name, imprint: run.product.imprint },
  }
}

const myTaskRank: Record<MyTask["status"], number> = { READY: 0, WAITING: 1, DONE: 2 }

function findChecklist(checklistId: string) {
  for (const run of demoProductionRuns) for (const item of run.items) for (const state of item.stageStates) {
    const checklist = state.checklists.find((candidate) => candidate.id === checklistId)
    if (checklist) return { run, checklist }
  }
  throw new DomainError("NOT_FOUND", "Controle não encontrado.")
}

function syncActiveDemoRun(product: CatalogProduct, before: CatalogProduct) {
  const run = product.currentRunId ? findProductionRun(product.currentRunId) : undefined
  if (!run) return
  const previousById = new Map(before.items.map((item) => [item.id, item]))
  const runByCatalogId = new Map(run.items.filter((item) => item.catalogItemId).map((item) => [item.catalogItemId!, item]))
  for (const catalogItem of product.items) {
    const operational = runByCatalogId.get(catalogItem.id)
    const previous = previousById.get(catalogItem.id)
    if (operational && previous?.itemType.id === catalogItem.itemType.id) {
      operational.name = catalogItem.name; operational.catalogSnapshot = structuredClone(catalogItem); operational.technicalValues = structuredClone(catalogItem.technicalValues); operational.version += 1
    } else {
      if (operational) { operational.status = "CANCELLED"; operational.cancelledAt = new Date().toISOString(); operational.version += 1 }
      const firstStage = run.stages[0]
      run.items.push({ id: `production-item-${crypto.randomUUID()}`, runId: run.id, catalogItemId: catalogItem.id, name: catalogItem.name, position: catalogItem.position, plannedQuantity: null, status: "ACTIVE", version: 1, itemType: catalogItem.itemType, catalogSnapshot: structuredClone(catalogItem), technicalValues: structuredClone(catalogItem.technicalValues), stageStates: firstStage ? [{ id: `state-${crypto.randomUUID()}`, stageId: firstStage.id, stageName: firstStage.name, stageShortName: firstStage.shortName, stageUsesPreviousDriveFolder: firstStage.usesPreviousDriveFolder, stagePosition: firstStage.position, status: "IN_PROGRESS", version: 1, enteredAt: new Date().toISOString(), checklists: [] }] : [] })
    }
  }
  const currentIds = new Set(product.items.map((item) => item.id))
  run.items.filter((item) => item.catalogItemId && !currentIds.has(item.catalogItemId)).forEach((item) => { item.status = "CANCELLED"; item.cancelledAt = new Date().toISOString(); item.version += 1 })
  run.product = { ...run.product, sku: product.sku ?? run.product.sku, name: product.name, imprint: product.imprint.name, description: product.description, status: product.status }
  run.changes.unshift({ id: `change-${crypto.randomUUID()}`, type: "CATALOG_UPDATED", before, after: structuredClone(product), actor: "Marina Rocha", createdAt: new Date().toISOString() })
  recalculateDemoRun(run)
}

function demoImprintMatchesKind(imprintId: string, kind?: ImprintKind) {
  if (!kind) return true
  const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === imprintId)
  return (imprint?.kind ?? "BARCA") === kind
}

export class DemoRepository implements EditorialRepository {
  private readonly driveFolders = new Map<string, string>()

  async searchCatalog(query: string, filters: CatalogFilters = {}, pagination: CatalogPagination = {}): Promise<CatalogPage<CatalogSearchResult>> {
    await wait(60)
    const term = normalizeTextSearch(query)
    const productsById = new Map(demoCatalogProducts.map((product) => [product.id, product]))
    const results = demoCatalogProducts.flatMap((product) => {
      if (filters.status && filters.status !== "ALL" && product.status !== filters.status) return []
      if (filters.imprintId && product.imprint.id !== filters.imprintId) return []
      if (!demoImprintMatchesKind(product.imprint.id, filters.imprintKind)) return []
      if (filters.collectionId === "NONE" && product.collection) return []
      if (filters.collectionId && filters.collectionId !== "NONE" && product.collection?.id !== filters.collectionId) return []
      if (filters.salesModel && product.salesModel !== filters.salesModel) return []
      if (filters.publicVisibility === "PUBLIC" && !product.publicPublishedAt) return []
      if (filters.publicVisibility === "PRIVATE" && product.publicPublishedAt) return []
      if (filters.publicationMonth && product.publicationMonth !== filters.publicationMonth) return []
      if (filters.publicationYear && product.publicationYear !== filters.publicationYear) return []
      const productText = normalizeTextSearch(`${product.sku ?? ""} ${product.name} ${product.author ?? ""} ${product.description ?? ""} ${product.imprint.name} ${product.collection?.name ?? ""}`)
      const results: CatalogSearchResult[] = []
      if (!term || productText.includes(term)) results.push({ entityType: "PRODUCT", productId: product.id, sku: product.sku, productName: product.name, author: product.author, title: product.name, subtitle: product.sku ? `${product.sku} · ${product.imprint.name}` : product.imprint.name, rank: term ? 1 : 0 })
      for (const item of product.items) {
        const text = normalizeTextSearch(`${productText} ${item.name} ${item.itemType.name} ${item.variant?.name ?? ""} ${item.variant?.externalId ?? ""} ${item.book?.isbn ?? ""} ${item.book?.credits.map((credit) => `${credit.contributor?.name ?? ""} ${credit.displayLabel}`).join(" ") ?? ""} ${item.technicalValues.map((value) => `${value.label} ${String(value.value ?? "")}`).join(" ")}`)
        if (!term || text.includes(term)) results.push({ entityType: "ITEM", productId: product.id, itemId: item.id, sku: product.sku, productName: product.name, author: product.author, title: item.name, subtitle: item.itemType.name, rank: term ? 1 : 0 })
      }
      return results
    })
    results.sort((left, right) => {
      const productOrder = compareCatalogProducts(productsById.get(left.productId)!, productsById.get(right.productId)!, filters.sort)
      if (productOrder) return productOrder
      if (left.entityType !== right.entityType) return left.entityType === "PRODUCT" ? -1 : 1
      return left.title.localeCompare(right.title) || (left.itemId ?? left.productId).localeCompare(right.itemId ?? right.productId)
    })
    const filteredCount = new Set(results.map((result) => result.productId)).size
    return paginateCatalog(results, demoCatalogProducts.length, filteredCount, pagination)
  }

  async listCatalogProducts(filters: CatalogFilters = {}, pagination: CatalogPagination = {}): Promise<CatalogPage<CatalogProductSummary>> {
    await wait(60)
    const searchMatches = filters.search
      ? new Set((await this.searchCatalog(filters.search, filters, { limit: 100 })).items.map((result) => result.productId))
      : undefined
    const filtered = demoCatalogProducts.filter((product) => {
      if (filters.status && filters.status !== "ALL" && product.status !== filters.status) return false
      if (filters.imprintId && product.imprint.id !== filters.imprintId) return false
      if (!demoImprintMatchesKind(product.imprint.id, filters.imprintKind)) return false
      if (filters.collectionId === "NONE" && product.collection) return false
      if (filters.collectionId && filters.collectionId !== "NONE" && product.collection?.id !== filters.collectionId) return false
      if (filters.salesModel && product.salesModel !== filters.salesModel) return false
      if (filters.publicVisibility === "PUBLIC" && !product.publicPublishedAt) return false
      if (filters.publicVisibility === "PRIVATE" && product.publicPublishedAt) return false
      if (filters.publicationMonth && product.publicationMonth !== filters.publicationMonth) return false
      if (filters.publicationYear && product.publicationYear !== filters.publicationYear) return false
      return !searchMatches || searchMatches.has(product.id)
    }).sort((left, right) => compareCatalogProducts(left, right, filters.sort)).map(catalogSummary)
    return paginateCatalog(filtered, demoCatalogProducts.length, filtered.length, pagination)
  }

  async listCatalogPublicationYears() {
    await wait(40)
    return [...new Set(demoCatalogProducts.map((product) => product.publicationYear).filter((year): year is number => year !== undefined))].sort((left, right) => right - left)
  }

  async listCatalogCollections(): Promise<CatalogCollectionSummary[]> {
    await wait(40)
    return structuredClone(demoCatalogCollections.map((collection) => ({
      ...collection,
      productCount: demoCatalogProducts.filter((product) => product.collection?.id === collection.id).length,
    })).sort((left, right) => left.imprint.name.localeCompare(right.imprint.name, "pt-BR") || left.name.localeCompare(right.name, "pt-BR")))
  }

  async createCatalogCollection(input: CatalogCollectionInput) {
    await wait()
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === input.imprintId && candidate.active)
    const name = input.name.trim()
    if (!imprint || name.length < 2 || name.length > 120 || (input.description?.length ?? 0) > 2000) throw new DomainError("VALIDATION_ERROR", "Informe selo, nome e descrição válidos.")
    if (demoCatalogCollections.some((collection) => collection.imprint.id === imprint.id && collection.name.toLocaleLowerCase("pt-BR") === name.toLocaleLowerCase("pt-BR"))) throw new DomainError("DUPLICATE_CATALOG_COLLECTION", "Já existe uma coleção com esse nome neste selo.")
    const timestamp = new Date().toISOString()
    const id = `collection-${crypto.randomUUID()}`
    demoCatalogCollections.push({ id, name, description: input.description?.trim() || undefined, active: true, version: 1, imprint: { id: imprint.id, code: imprint.code, name: imprint.name }, productCount: 0, createdAt: timestamp, updatedAt: timestamp })
    return id
  }

  async updateCatalogCollection(collectionId: string, input: CatalogCollectionInput, expectedVersion: number) {
    await wait()
    const collection = demoCatalogCollections.find((candidate) => candidate.id === collectionId)
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === input.imprintId)
    const name = input.name.trim()
    if (!collection) throw new DomainError("NOT_FOUND", "Coleção não encontrada.")
    if (collection.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A coleção foi alterada por outra pessoa.")
    if (!imprint || name.length < 2 || name.length > 120 || (input.description?.length ?? 0) > 2000) throw new DomainError("VALIDATION_ERROR", "Informe selo, nome e descrição válidos.")
    if (collection.imprint.id !== imprint.id && demoCatalogProducts.some((product) => product.collection?.id === collectionId)) throw new DomainError("CATALOG_COLLECTION_HAS_PRODUCTS", "A coleção possui produtos associados.")
    if (demoCatalogCollections.some((candidate) => candidate.id !== collectionId && candidate.imprint.id === imprint.id && candidate.name.toLocaleLowerCase("pt-BR") === name.toLocaleLowerCase("pt-BR"))) throw new DomainError("DUPLICATE_CATALOG_COLLECTION", "Já existe uma coleção com esse nome neste selo.")
    collection.name = name; collection.description = input.description?.trim() || undefined; collection.imprint = { id: imprint.id, code: imprint.code, name: imprint.name }; collection.version += 1; collection.updatedAt = new Date().toISOString()
    demoCatalogProducts.filter((product) => product.collection?.id === collectionId).forEach((product) => { product.collection = { id: collection.id, name: collection.name, active: collection.active } })
  }

  async setCatalogCollectionActive(collectionId: string, active: boolean, reason: string, expectedVersion: number) {
    await wait()
    const collection = demoCatalogCollections.find((candidate) => candidate.id === collectionId)
    if (!collection) throw new DomainError("NOT_FOUND", "Coleção não encontrada.")
    if (collection.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A coleção foi alterada por outra pessoa.")
    if (!reason.trim()) throw new DomainError("REASON_REQUIRED", "Informe o motivo da alteração.")
    collection.active = active; collection.version += 1; collection.updatedAt = new Date().toISOString()
    demoCatalogProducts.filter((product) => product.collection?.id === collectionId).forEach((product) => { product.collection = { id: collection.id, name: collection.name, active } })
  }

  async deleteCatalogCollection(collectionId: string) {
    await wait()
    const index = demoCatalogCollections.findIndex((candidate) => candidate.id === collectionId)
    if (index < 0) throw new DomainError("NOT_FOUND", "Coleção não encontrada.")
    if (demoCatalogProducts.some((product) => product.collection?.id === collectionId)) throw new DomainError("CATALOG_COLLECTION_HAS_PRODUCTS", "A coleção possui produtos associados.")
    demoCatalogCollections.splice(index, 1)
  }

  async getCatalogProduct(productId: string) {
    await wait(50)
    const product = structuredClone(findCatalogProduct(productId))
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === product.imprint.id)
    product.imprint = { ...product.imprint, driveRootFolderUrl: imprint?.driveRootFolderUrl }
    product.completedProductionCount = demoProductionRuns.filter((run) => run.product.id === productId && run.status === "COMPLETED").length
    return product
  }

  async getCatalogItem(itemId: string) {
    await wait(50)
    return structuredClone(findCatalogItem(itemId).item)
  }

  async listCatalogItemActivity(itemId: string, limit = 20, offset = 0) {
    await wait(40)
    findCatalogItem(itemId)
    const start = Math.max(offset, 0)
    return structuredClone((demoCatalogItemActivities.get(itemId) ?? []).slice(start, start + Math.min(Math.max(limit, 1), 100)))
  }

  async getCatalogItemEditionSnapshot(snapshotId: string) {
    await wait(40)
    const snapshot = demoCatalogItemEditionSnapshots.get(snapshotId)
    if (!snapshot) throw new DomainError("NOT_FOUND", "Edição anterior não encontrada.")
    return structuredClone(snapshot)
  }

  async createCatalogProduct(input: CreateCatalogProductInput) {
    await wait()
    if (input.status !== "DRAFT" && input.status !== "READY") throw new DomainError("CATALOG_STATUS_MANAGED_BY_PRODUCTION", "Crie o produto como rascunho ou pronto.")
    validateCatalogProductCreation(input)
    const normalizedSku = input.sku?.trim().toUpperCase() ?? ""
    if (normalizedSku && demoCatalogProducts.some((product) => product.sku?.toLowerCase() === normalizedSku.toLowerCase())) throw new DomainError("DUPLICATE_SKU", "Já existe um produto com este SKU.")
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === input.imprintId)
    if (!imprint) throw new DomainError("VALIDATION_ERROR", "Selecione um selo válido.")
    const id = `catalog-${crypto.randomUUID()}`
    const timestamp = new Date().toISOString()
    demoCatalogProducts.unshift({
      id, sku: normalizedSku || undefined, name: input.name.trim(), author: input.author?.trim() || undefined, description: input.description?.trim(), status: input.status,
      version: 1, imprint: { id: imprint.id, code: imprint.code, name: imprint.name }, publicationMonth: input.publicationMonth,
      collection: input.collectionId ? (() => { const collection = demoCatalogCollections.find((candidate) => candidate.id === input.collectionId)!; return { id: collection.id, name: collection.name, active: collection.active } })() : undefined,
      publicationYear: input.publicationYear, salesModel: input.salesModel, productionCount: 0,
      photosUrl: input.photosUrl?.trim() || undefined, images: [], items: input.items.map((item, index) => catalogItemFromInput(id, item, index + 1)), createdAt: timestamp, updatedAt: timestamp,
    })
    return id
  }

  async saveCatalogProduct(productId: string, input: SaveCatalogProductInput, expectedVersion: number, confirmActiveProduction = false) {
    await wait()
    const product = findCatalogProduct(productId)
    if (product.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O produto foi alterado por outra pessoa.")
    const nextStatus = input.status ?? product.status
    const allowedStatusChange = product.productionCount === 0
      && ((product.status === "DRAFT" && nextStatus === "READY") || (product.status === "READY" && nextStatus === "DRAFT"))
    if (nextStatus !== product.status && !allowedStatusChange) throw new DomainError("CATALOG_STATUS_MANAGED_BY_PRODUCTION", "O status só pode ser alternado entre Rascunho e Pronto antes da primeira produção.")
    validateCatalogProductInput(input)
    if (product.status === "IN_PRODUCTION" && !confirmActiveProduction) throw new DomainError("ACTIVE_PRODUCTION_CONFIRMATION_REQUIRED", "Produto em produção. Alterar mesmo assim?")
    const normalizedSku = input.sku?.trim().toUpperCase() ?? ""
    if (normalizedSku && demoCatalogProducts.some((candidate) => candidate.id !== productId && candidate.sku?.toLowerCase() === normalizedSku.toLowerCase())) throw new DomainError("DUPLICATE_SKU", "Já existe um produto com este SKU.")
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === input.imprintId)
    if (!imprint) throw new DomainError("VALIDATION_ERROR", "Selecione um selo válido.")
    const before = structuredClone(product)
    product.sku = normalizedSku || undefined; product.name = input.name.trim(); product.author = input.author?.trim() || undefined; product.description = input.description?.trim(); product.status = nextStatus
    product.publicationMonth = input.publicationMonth; product.publicationYear = input.publicationYear
    product.photosUrl = input.photosUrl?.trim() || undefined
    product.driveFolderUrl = normalizeDriveFolderUrl(input.driveFolderUrl)
    if (input.salesModel) product.salesModel = input.salesModel
    product.imprint = { id: imprint.id, code: imprint.code, name: imprint.name }
    product.collection = input.collectionId ? (() => { const collection = demoCatalogCollections.find((candidate) => candidate.id === input.collectionId)!; return { id: collection.id, name: collection.name, active: collection.active } })() : undefined
    product.items = input.items.map((item, index) => {
      const previous = product.items.find((candidate) => candidate.id === item.id)
      return previous && previous.itemType.id === item.itemTypeId
        ? catalogItemFromInput(product.id, item, index + 1, previous)
        : catalogItemFromInput(product.id, { ...item, id: undefined }, index + 1)
    })
    product.version += 1; product.updatedAt = new Date().toISOString()
    if (product.status === "IN_PRODUCTION") syncActiveDemoRun(product, before)
    return structuredClone(product)
  }

  async saveCatalogItem(itemId: string, input: CatalogItemInput, expectedVersion: number, confirmActiveProduction = false) {
    await wait()
    const { product, item } = findCatalogItem(itemId)
    if (item.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado por outra pessoa.")
    if (product.status === "IN_PRODUCTION" && !confirmActiveProduction) throw new DomainError("ACTIVE_PRODUCTION_CONFIRMATION_REQUIRED", "Produto em produção. Alterar mesmo assim?")
    if (input.id !== itemId || !input.name.trim()) throw new DomainError("VALIDATION_ERROR", "Informe os dados obrigatórios do item.")
    const beforeProduct = structuredClone(product)
    const saved = catalogItemFromInput(product.id, input, item.position, item)
    product.items[product.items.findIndex((candidate) => candidate.id === itemId)] = saved
    product.version += 1
    product.updatedAt = new Date().toISOString()
    recordCatalogItemActivity(itemId, { type: "CATALOG_ITEM_UPDATED", title: "Dados do item atualizados" })
    if (product.status === "IN_PRODUCTION") syncActiveDemoRun(product, beforeProduct)
    return structuredClone(saved)
  }

  async createCatalogItemEdition(itemId: string, expectedVersion: number, confirmActiveProduction = false) {
    await wait()
    const { product, item } = findCatalogItem(itemId)
    if (item.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado por outra pessoa.")
    if (!item.itemType.isBook) throw new DomainError("BOOK_ITEM_REQUIRED", "Somente livros e livretos podem ter novas edições.")
    if (product.status === "IN_PRODUCTION" && !confirmActiveProduction) throw new DomainError("ACTIVE_PRODUCTION_CONFIRMATION_REQUIRED", "Produto em produção. Criar a nova edição mesmo assim?")
    const timestamp = new Date().toISOString()
    const snapshotId = `catalog-edition-snapshot-${crypto.randomUUID()}`
    demoCatalogItemEditionSnapshots.set(snapshotId, {
      id: snapshotId,
      itemId,
      editionSequence: item.currentEditionSequence,
      editionLabel: item.book?.editionLabel,
      sourceItemVersion: item.version,
      snapshotSchemaVersion: 1,
      snapshotData: catalogItemSnapshotData(item),
      createdBy: demoCatalogActor,
      createdAt: timestamp,
    })
    item.currentEditionSequence += 1
    item.version += 1
    product.version += 1
    product.updatedAt = timestamp
    recordCatalogItemActivity(itemId, { type: "CATALOG_ITEM_EDITION_CREATED", title: "Nova edição criada", snapshotId, editionLabel: item.book?.editionLabel, createdAt: timestamp })
    if (product.status === "IN_PRODUCTION" && product.currentRunId) {
      const run = findProductionRun(product.currentRunId)
      const operational = run.items.find((candidate) => candidate.catalogItemId === item.id && candidate.status !== "CANCELLED")
      if (operational) { operational.catalogSnapshot = structuredClone(item); operational.version += 1 }
      run.changes.unshift({ id: `change-${crypto.randomUUID()}`, type: "CATALOG_ITEM_EDITION_CREATED", before: demoCatalogItemEditionSnapshots.get(snapshotId)?.snapshotData, after: structuredClone(item), actor: demoCatalogActor.name, createdAt: timestamp })
      run.version += 1
    }
    return structuredClone(item)
  }

  async deleteCatalogProduct(productId: string, expectedVersion: number): Promise<DeleteCatalogProductResult> {
    await wait()
    const product = findCatalogProduct(productId)
    if (product.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O produto foi alterado por outra pessoa.")
    if (product.productionCount > 0) throw new DomainError("PRODUCT_HAS_PRODUCTION_HISTORY", "Produtos com histórico de produção não podem ser excluídos.")
    demoCatalogProducts.splice(demoCatalogProducts.findIndex((candidate) => candidate.id === productId), 1)
    return { storageCleanupFailed: false }
  }

  async listProductionPrintItems(runIds: string[]): Promise<ProductionPrintItem[]> {
    await wait(60)
    const ids = new Set(runIds)
    return demoProductionRuns.filter((run) => ids.has(run.id)).flatMap((run) => run.items.filter((item) => item.status !== "CANCELLED").map((item) => {
      const current = item.stageStates.filter((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED").sort((first, second) => first.stagePosition - second.stagePosition)[0]
      return {
        runId: run.id, itemId: item.id, name: item.name, position: item.position, status: item.status,
        printShop: item.printShop ? { id: item.printShop.id, name: item.printShop.name } : null,
        quantity: item.plannedQuantity ?? run.kitQuantity ?? null, receivedQuantity: item.receivedQuantity ?? null,
        estimatedDeliveryDate: item.estimatedDeliveryDate ?? null, finalDeliveryDate: item.finalDeliveryDate ?? null, quoteUrl: item.quoteUrl ?? null,
        printChecklist: [...(item.printChecklist ?? [])],
        currentStage: current ? { name: current.stageName, shortName: current.stageShortName, position: current.stagePosition, blocked: current.status === "BLOCKED" } : null,
      }
    }).sort((first, second) => first.position - second.position))
  }

  async listProductionRuns(filters: import("@/types/domain").ProductionFilters = {}) {
    await wait(60)
    const searchMatches = filters.search
      ? new Set((await this.searchCatalog(filters.search, {}, { limit: 100 })).items.map((result) => result.productId))
      : undefined
    return structuredClone(demoProductionRuns.filter((run) => {
      if (filters.status && filters.status !== "ALL" && run.status !== filters.status) return false
      if (filters.ledByMe && run.lead?.id !== "demo-admin") return false
      if (filters.imprintId && findCatalogProduct(run.product.id).imprint.id !== filters.imprintId) return false
      if (!demoImprintMatchesKind(findCatalogProduct(run.product.id).imprint.id, filters.imprintKind)) return false
      if (filters.printShopId && !run.items.some((item) => item.status !== "CANCELLED" && item.printShop?.id === filters.printShopId)) return false
      if (filters.month && Number(run.referenceMonth.slice(5, 7)) !== filters.month) return false
      if (filters.year && Number(run.referenceMonth.slice(0, 4)) !== filters.year) return false
      if (filters.reprint === "ONLY" && !run.isReprint) return false
      if (filters.reprint === "EXCLUDE" && run.isReprint) return false
      return !searchMatches || searchMatches.has(run.product.id)
    }).map(productionSummary))
  }

  async getProductionRun(runId: string) {
    await wait(50)
    return structuredClone(findProductionRun(runId))
  }

  async startProduction(input: StartProductionInput) {
    await wait()
    const product = findCatalogProduct(input.productId)
    if (product.status !== "DRAFT" && product.status !== "READY") throw new DomainError("STARTABLE_CATALOG_PRODUCT_REQUIRED", "Somente produtos em rascunho ou prontos podem iniciar uma produção.")
    const latestRun = latestDemoRunForProduct(product.id)
    if (latestRun && (latestRun.status === "ACTIVE" || latestRun.status === "STUCK" || latestRun.status === "CANCELLED")) throw new DomainError("OPEN_PRODUCTION_EXISTS", "Este produto já possui uma produção atual.")
    if (!product.items.length) throw new DomainError("ITEMS_REQUIRED", "Adicione pelo menos um item à composição.")
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(input.referenceMonth)) throw new DomainError("VALIDATION_ERROR", "Informe uma competência válida.")
    if (!isIsoDate(input.targetEndDate)) throw new DomainError("INVALID_TARGET_END_DATE", "Informe a data final prevista.")
    const stageDays = input.stageDays ?? []
    if (new Set(stageDays.map(({ stageId }) => stageId)).size !== stageDays.length || stageDays.some(({ days }) => !(days >= 0 && days <= 999 && Math.round(days * 100) === days * 100))) throw new DomainError("INVALID_STAGE_DAYS", "Revise as durações ajustadas das etapas.")
    const pipeline = demoPipelineCatalog.flatMap((entry) => entry.versions.map((version) => ({ entry, version }))).find(({ version }) => version.id === input.pipelineVersionId)
    if (pipeline && !pipeline.entry.active) throw new DomainError("ACTIVE_PIPELINE_REQUIRED", "Reative o fluxo de trabalho antes de usá-lo em uma nova produção.")
    if (!pipeline?.version.publishedAt) throw new DomainError("VALIDATION_ERROR", "Selecione um fluxo de trabalho publicado.")
    const stageAssignments = input.stageAssignments ?? []
    if (new Set(stageAssignments.map(({ stageId }) => stageId)).size !== stageAssignments.length || stageAssignments.some(({ stageId }) => !pipeline.version.stages.some((stage) => stage.id === stageId))) throw new DomainError("INVALID_STAGE_ASSIGNMENTS", "Revise os responsáveis: cada etapa aceita um administrador ou operador.")
    const assignees = stageAssignments.map(({ stageId, userId }) => ({ stageId, user: demoAssignableUser(userId) }))
    const lead = input.leadUserId ? demoAssignableUser(input.leadUserId, "INVALID_PRODUCTION_LEAD") : undefined
    const id = `run-${crypto.randomUUID()}`
    const timestamp = new Date().toISOString()
    const stages = pipeline.version.stages.map(({ id: stageId, name, shortName, position, usesPreviousDriveFolder }) => ({ id: stageId, name, shortName, position, usesPreviousDriveFolder, manualDays: stageDays.find((entry) => entry.stageId === stageId)?.days ?? null }))
    const run: import("@/types/domain").ProductionRun = {
      id, sequence: product.productionCount + 1, referenceMonth: input.referenceMonth, status: "ACTIVE", version: 1,
      sourceCatalogStatus: product.status, targetEndDate: input.targetEndDate, isReprint: input.isReprint ?? false,
      startedAt: timestamp, product: { id: product.id, sku: product.sku, name: product.name, imprint: product.imprint.name, description: product.description, status: "IN_PRODUCTION" },
      pipeline: { id: pipeline.version.id, templateId: pipeline.entry.id, name: pipeline.entry.name, version: pipeline.version.version }, stages,
      items: product.items.map((item) => {
        const applicable = pipeline.version.stages.filter((stage) => stage.applicableItemTypeIds.includes(item.itemType.id))
        if (!applicable.length) throw new DomainError("PIPELINE_DOES_NOT_COVER_ITEM", `O fluxo de trabalho não possui etapa para ${item.name}.`)
        return { id: `production-item-${crypto.randomUUID()}`, runId: id, catalogItemId: item.id, name: item.name, position: item.position, status: "ACTIVE" as const, version: 1, itemType: item.itemType, catalogSnapshot: structuredClone(item), technicalValues: structuredClone(item.technicalValues), stageStates: applicable.map((stage, index) => ({ id: `state-${crypto.randomUUID()}`, stageId: stage.id, stageName: stage.name, stageShortName: stage.shortName, stageUsesPreviousDriveFolder: stage.usesPreviousDriveFolder, stagePosition: stage.position, status: index === 0 ? "IN_PROGRESS" as const : "NOT_STARTED" as const, version: 1, enteredAt: index === 0 ? new Date().toISOString() : undefined, checklists: stage.checklists.filter((check) => !check.itemTypeIds.length || check.itemTypeIds.includes(item.itemType.id)).map((check) => ({ id: `check-${crypto.randomUUID()}`, label: check.label, required: check.required, position: check.position, status: "PENDING" as const, version: 1 })) })) }
      }),
      gates: pipeline.version.gates.map((gate) => ({ id: `gate-${crypto.randomUUID()}`, label: gate.label, required: gate.required, status: "PENDING", version: 1 })), followers: [{ id: "demo-admin", name: "Marina Rocha" }, ...[...(lead ? [lead] : []), ...assignees.map(({ user }) => user)].filter((user, index, users) => user.id !== "demo-admin" && users.findIndex((candidate) => candidate.id === user.id) === index).map((user) => ({ id: user.id, name: user.name }))],
      lead: lead ? { id: lead.id, name: lead.name } : null,
      assignments: stages.flatMap((stage) => assignees.filter((assignee) => assignee.stageId === stage.id).map(({ user }) => ({ stageId: stage.id, user: { id: user.id, name: user.name }, assignedAt: timestamp }))), changes: [],
    }
    scheduleDemoRun(run)
    demoProductionRuns.unshift(run); product.status = "IN_PRODUCTION"; product.currentRunId = id; product.productionCount += 1; product.version += 1; product.updatedAt = timestamp
    return id
  }

  async cancelProduction(runId: string, expectedVersion: number, reason: string) {
    await wait()
    const run = findProductionRun(runId)
    if (!reason.trim()) throw new DomainError("REASON_REQUIRED", "Informe uma justificativa.")
    if (run.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A produção foi alterada por outra pessoa.")
    if (run.status !== "ACTIVE" && run.status !== "STUCK") throw new DomainError("OPEN_PRODUCTION_REQUIRED", "A produção não está aberta.")
    const timestamp = new Date().toISOString()
    run.status = "CANCELLED"; run.stoppedAt = undefined; run.cancelledAt = timestamp; run.product.status = "CANCELLED"; run.version += 1
    const product = findCatalogProduct(run.product.id)
    product.status = "CANCELLED"; product.currentRunId = run.id; product.version += 1; product.updatedAt = timestamp
    return structuredClone(run)
  }

  async updateProduction(input: UpdateProductionInput) {
    await wait()
    const run = findProductionRun(input.runId)
    if (run.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A produção foi alterada por outra pessoa.")
    if (run.status !== "ACTIVE") throw new DomainError("OPEN_PRODUCTION_REQUIRED", "Somente produções ativas podem ser alteradas.")
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(input.referenceMonth)) throw new DomainError("VALIDATION_ERROR", "Informe uma competência válida.")
    if (input.targetEndDate && !isIsoDate(input.targetEndDate)) throw new DomainError("INVALID_TARGET_END_DATE", "Informe uma data final prevista válida.")
    const before = { referenceMonth: run.referenceMonth, targetEndDate: run.targetEndDate ?? null, isReprint: run.isReprint ?? false }
    const targetEndDate = input.targetEndDate || run.targetEndDate || null
    run.referenceMonth = input.referenceMonth
    run.isReprint = input.isReprint ?? before.isReprint
    if (targetEndDate !== before.targetEndDate) { run.targetEndDate = targetEndDate; scheduleDemoRun(run) }
    run.version += 1
    run.changes.unshift({
      id: `production-change-${crypto.randomUUID()}`,
      type: "PRODUCTION_UPDATED",
      actor: "Marina Rocha",
      createdAt: new Date().toISOString(),
      before,
      after: { referenceMonth: run.referenceMonth, targetEndDate: run.targetEndDate ?? null, isReprint: run.isReprint },
    })
    return structuredClone(run)
  }

  async updateProductionQuantities(input: import("@/types/domain").UpdateProductionQuantitiesInput) {
    await wait()
    const run = findProductionRun(input.runId)
    assertActiveDemoRun(run)
    if (run.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A produção foi alterada por outra pessoa.")
    if (input.kitQuantity != null && (!Number.isInteger(input.kitQuantity) || input.kitQuantity <= 0)) throw new DomainError("VALIDATION_ERROR", "Informe uma quantidade global válida.")
    const editableItems = run.items.filter((item) => item.status !== "CANCELLED")
    if (input.itemQuantities.length !== editableItems.length || new Set(input.itemQuantities.map(({ itemId }) => itemId)).size !== editableItems.length) throw new DomainError("VALIDATION_ERROR", "Informe todos os itens da produção.")
    const quantities = new Map(input.itemQuantities.map(({ itemId, quantity }) => [itemId, quantity]))
    if (editableItems.some((item) => !quantities.has(item.id)) || [...quantities.values()].some((quantity) => quantity != null && (!Number.isInteger(quantity) || quantity <= 0))) throw new DomainError("VALIDATION_ERROR", "Informe quantidades válidas para os itens.")
    const before = { kitQuantity: run.kitQuantity, itemQuantities: editableItems.map((item) => ({ itemId: item.id, quantity: item.plannedQuantity })) }
    run.kitQuantity = input.kitQuantity
    editableItems.forEach((item) => { item.plannedQuantity = quantities.get(item.id); item.version += 1 })
    run.version += 1
    run.changes.unshift({ id: `production-change-${crypto.randomUUID()}`, type: "PRODUCTION_QUANTITIES_UPDATED", actor: "Marina Rocha", createdAt: new Date().toISOString(), before, after: { kitQuantity: run.kitQuantity, itemQuantities: editableItems.map((item) => ({ itemId: item.id, quantity: item.plannedQuantity })) } })
    return structuredClone(run)
  }

  async stopProduction(runId: string, expectedVersion: number, reason: string) {
    await wait()
    const run = findProductionRun(runId)
    if (!reason.trim()) throw new DomainError("REASON_REQUIRED", "Informe uma justificativa.")
    if (run.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A produção foi alterada por outra pessoa.")
    assertActiveDemoRun(run)
    const product = findCatalogProduct(run.product.id)
    const timestamp = new Date().toISOString()
    run.status = "STUCK"; run.stoppedAt = timestamp; run.product.status = "STUCK"; run.version += 1
    product.status = "STUCK"; product.version += 1; product.updatedAt = new Date().toISOString()
    run.changes.unshift({ id: `production-change-${crypto.randomUUID()}`, type: "PRODUCTION_STOPPED", reason: reason.trim(), actor: "Marina Rocha", createdAt: timestamp })
    return structuredClone(run)
  }

  async resumeProduction(runId: string, expectedVersion: number) {
    await wait()
    const run = findProductionRun(runId)
    if (run.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A produção foi alterada por outra pessoa.")
    if (run.status !== "STUCK" && run.status !== "CANCELLED") throw new DomainError("RESUMABLE_PRODUCTION_REQUIRED", "A produção não pode ser retomada.")
    if (latestDemoRunForProduct(run.product.id)?.id !== run.id) throw new DomainError("LATEST_PRODUCTION_REQUIRED", "Somente a produção mais recente pode ser retomada.")
    const product = findCatalogProduct(run.product.id)
    run.status = "ACTIVE"; run.stoppedAt = undefined; run.cancelledAt = undefined; run.product.status = "IN_PRODUCTION"; run.version += 1
    product.status = "IN_PRODUCTION"; product.version += 1; product.updatedAt = new Date().toISOString()
    run.changes.unshift({ id: `production-change-${crypto.randomUUID()}`, type: "PRODUCTION_RESUMED", actor: "Marina Rocha", createdAt: new Date().toISOString() })
    recalculateDemoRun(run)
    return structuredClone(run)
  }

  async deleteProduction(runId: string, expectedVersion: number) {
    await wait()
    const runIndex = demoProductionRuns.findIndex((run) => run.id === runId)
    if (runIndex < 0) throw new DomainError("NOT_FOUND", "Produção não encontrada.")
    const run = demoProductionRuns[runIndex]
    if (run.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A produção foi alterada por outra pessoa.")
    if (run.status !== "CANCELLED") throw new DomainError("PRODUCTION_MUST_BE_CANCELLED", "Cancele a produção antes de apagá-la.")
    const product = findCatalogProduct(run.product.id)
    if (latestDemoRunForProduct(product.id)?.id !== run.id) throw new DomainError("LATEST_PRODUCTION_REQUIRED", "Somente a produção cancelada mais recente pode ser apagada.")
    demoProductionRuns.splice(runIndex, 1)
    const remaining = demoProductionRuns.filter((candidate) => candidate.product.id === product.id)
    product.status = run.sourceCatalogStatus ?? "DRAFT"
    product.currentRunId = undefined
    product.productionCount = remaining.length
    product.version += 1
    product.updatedAt = new Date().toISOString()
    remaining.forEach((candidate) => { candidate.product.status = product.status })
  }

  async followProduction(runId: string, follow: boolean) {
    const run = findProductionRun(runId)
    const index = run.followers.findIndex((user) => user.id === "demo-admin")
    if (follow && index < 0) run.followers.push({ id: "demo-admin", name: "Marina Rocha" })
    if (!follow && index >= 0) run.followers.splice(index, 1)
  }

  async returnProductionItem(input: ReturnProductionItemInput) {
    const run = findProductionRun(input.runId)
    assertActiveDemoRun(run)
    if (!input.reason.trim()) throw new DomainError("REASON_REQUIRED", "Informe o motivo do retorno.")
    const item = run.items.find((candidate) => candidate.id === input.itemId)
    if (!item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    if (item.status === "CANCELLED") throw new DomainError("ITEM_CANCELLED", "O item foi cancelado.")
    // Item concluído: qualquer etapa pode ser reaberta, inclusive a última.
    const completed = item.status === "COMPLETED"
    const current = completed ? item.stageStates.reduce<typeof item.stageStates[number] | undefined>((last, state) => !last || state.stagePosition > last.stagePosition ? state : last, undefined) : item.stageStates.find((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED")
    if (!current) throw new DomainError("CURRENT_STAGE_REQUIRED", "O item precisa ter uma etapa em andamento.")
    if (current.status === "BLOCKED") throw new DomainError("ITEM_BLOCKED", "Desbloqueie o item antes de voltar uma etapa.")
    const target = item.stageStates.find((state) => state.stageId === input.targetStageId)
    if (!target || target.stagePosition > current.stagePosition || (!completed && target.stagePosition === current.stagePosition)) throw new DomainError("INVALID_STAGE_TRANSITION", "Selecione uma etapa anterior aplicável ao item.")
    const before = { item: structuredClone(item), fromStage: { id: current.stageId, name: current.stageName }, toStage: { id: target.stageId, name: target.stageName } }
    const timestamp = new Date().toISOString()
    for (const state of item.stageStates.filter((state) => state.stagePosition >= target.stagePosition)) {
      state.status = state.id === target.id ? "IN_PROGRESS" : "NOT_STARTED"
      state.enteredAt = state.id === target.id ? timestamp : undefined
      state.completedAt = undefined; state.blockedAt = undefined; state.blockedReason = undefined; state.version += 1
      for (const checklist of state.checklists) { checklist.status = "PENDING"; checklist.reason = undefined; checklist.version += 1 }
    }
    item.status = "ACTIVE"; item.version += 1
    run.changes.unshift({ id: `change-${crypto.randomUUID()}`, type: "ITEM_STAGE_RETURNED", reason: input.reason.trim(), before,
      after: { item: structuredClone(item), fromStage: before.fromStage, toStage: before.toStage }, actor: demoCatalogActor.name, createdAt: timestamp })
    recalculateDemoRun(run)
    return structuredClone(run)
  }

  async transitionProductionItem(input: TransitionItemInput) {
    const run = findProductionRun(input.runId); const item = run.items.find((candidate) => candidate.id === input.itemId)
    assertActiveDemoRun(run)
    if (!item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    const current = item.stageStates.find((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED")
    const target = item.stageStates.find((state) => state.stageId === input.targetStageId)
    if (!current || !target || current.status === "BLOCKED" || current.checklists.some((check) => check.required && check.status !== "DONE")) throw new DomainError("BLOCKING_CHECKLISTS", "Conclua os controles obrigatórios antes de avançar.")
    const timestamp = new Date().toISOString()
    current.status = "DONE"; current.completedAt = timestamp; current.version += 1
    if (target.id === current.id && !item.stageStates.some((state) => state.stagePosition > current.stagePosition)) item.status = "COMPLETED"
    else { target.status = "IN_PROGRESS"; target.enteredAt = new Date().toISOString(); target.version += 1 }
    item.version += 1; recalculateDemoRun(run); return structuredClone(run)
  }

  async listAssignableUsers(): Promise<AssignableUser[]> {
    await wait(40)
    return demoWorkspaceUsers
      .filter((user): user is typeof user & { role: AssignableUser["role"] } => user.role === "ADMIN" || user.role === "OPS")
      .map(({ id, name, role, functions, imprintIds }) => ({ id, name, role, functions: [...functions], imprintIds: [...imprintIds] }))
      .sort((left, right) => left.name.localeCompare(right.name, "pt-BR"))
  }

  async setProductionStageAssignment(input: SetProductionStageAssignmentInput) {
    await wait()
    const run = findProductionRun(input.runId)
    if (run.status !== "ACTIVE" && run.status !== "STUCK") throw new DomainError("OPEN_PRODUCTION_REQUIRED", "A produção não está aberta.")
    const stage = run.stages.find((candidate) => candidate.id === input.stageId)
    if (!stage) throw new DomainError("INVALID_STAGE", "A etapa não pertence ao fluxo de trabalho desta produção.")
    const remaining = run.assignments.filter((assignment) => assignment.stageId !== stage.id)
    if (input.userId) {
      const user = demoAssignableUser(input.userId)
      const current = run.assignments.find((assignment) => assignment.stageId === stage.id)
      remaining.push(current?.user.id === user.id ? current : { stageId: stage.id, user: { id: user.id, name: user.name }, assignedAt: new Date().toISOString() })
      if (!run.followers.some((follower) => follower.id === user.id)) run.followers.push({ id: user.id, name: user.name })
    }
    const positions = new Map(run.stages.map((candidate) => [candidate.id, candidate.position]))
    run.assignments = remaining.sort((left, right) => (positions.get(left.stageId) ?? 0) - (positions.get(right.stageId) ?? 0))
    return structuredClone(run)
  }

  async setProductionLead(input: SetProductionLeadInput) {
    await wait()
    const run = findProductionRun(input.runId)
    if (run.status !== "ACTIVE" && run.status !== "STUCK") throw new DomainError("OPEN_PRODUCTION_REQUIRED", "A produção não está aberta.")
    if (!input.userId) { run.lead = null; return structuredClone(run) }
    const user = demoAssignableUser(input.userId, "INVALID_PRODUCTION_LEAD")
    run.lead = { id: user.id, name: user.name }
    if (!run.followers.some((follower) => follower.id === user.id)) run.followers.push({ id: user.id, name: user.name })
    return structuredClone(run)
  }

  async saveProductionSchedule(input: import("@/types/domain").SaveProductionScheduleInput) {
    await wait()
    const run = findProductionRun(input.runId)
    if (run.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A produção foi alterada por outra pessoa.")
    assertActiveDemoRun(run)
    if (!isIsoDate(input.targetEndDate)) throw new DomainError("INVALID_TARGET_END_DATE", "Informe a data final prevista.")
    if (!input.reason?.trim()) throw new DomainError("REASON_REQUIRED", "Informe o motivo do ajuste.")
    const applicableIds = new Set(demoApplicableStages(run).map((stage) => stage.id))
    if (new Set(input.stageDays.map(({ stageId }) => stageId)).size !== input.stageDays.length
      || input.stageDays.some(({ stageId, days }) => !applicableIds.has(stageId) || (days !== null && !(days >= 0 && days <= 999 && Math.round(days * 100) === days * 100))))
      throw new DomainError("INVALID_STAGE_DAYS", "Revise as durações das etapas.")
    const snapshot = () => ({ targetEndDate: run.targetEndDate ?? null, stages: demoApplicableStages(run).map((stage) => ({ stageId: stage.id, stageName: stage.name, estimatedDays: stage.estimatedDays ?? null, manualDays: stage.manualDays ?? null, plannedStartDate: stage.plannedStartDate ?? null, plannedEndDate: stage.plannedEndDate ?? null })) })
    const before = snapshot()
    const days = new Map(input.stageDays.map(({ stageId, days: value }) => [stageId, value]))
    run.targetEndDate = input.targetEndDate
    run.stages = run.stages.map((stage) => days.has(stage.id) ? { ...stage, manualDays: days.get(stage.id) ?? null } : stage)
    scheduleDemoRun(run)
    const after = snapshot()
    if (JSON.stringify(after) === JSON.stringify(before)) return structuredClone(run)
    run.version += 1
    run.changes.unshift({ id: `production-change-${crypto.randomUUID()}`, type: "PRODUCTION_SCHEDULE_UPDATED", reason: input.reason.trim(), actor: "Marina Rocha", createdAt: new Date().toISOString(), before, after })
    return structuredClone(run)
  }

  async listMyTasks(): Promise<MyTask[]> {
    await wait(60)
    return demoProductionRuns
      .filter((run) => run.status === "ACTIVE" || run.status === "STUCK")
      .flatMap((run) => run.assignments.filter((assignment) => assignment.user.id === "demo-admin").map((assignment) => demoTaskFor(run, assignment)))
      .filter((task): task is MyTask => Boolean(task))
      .sort((left, right) => myTaskRank[left.status] - myTaskRank[right.status]
        || (left.plannedEndDate ?? "\uffff").localeCompare(right.plannedEndDate ?? "\uffff")
        || (left.readySince ?? "\uffff").localeCompare(right.readySince ?? "\uffff")
        || left.run.referenceMonth.localeCompare(right.run.referenceMonth)
        || left.stage.position - right.stage.position)
  }

  async skipProductionItem(input: SkipProductionItemInput) {
    const run = findProductionRun(input.runId); const item = run.items.find((candidate) => candidate.id === input.itemId)
    assertActiveDemoRun(run)
    if (!input.reason?.trim()) throw new DomainError("REASON_REQUIRED", "Informe o motivo para pular a etapa.")
    if (!item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    const current = item.stageStates.find((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED")
    const target = item.stageStates.find((state) => state.stageId === input.targetStageId)
    if (!current || !target || current.status === "BLOCKED") throw new DomainError("INVALID_TRANSITION", "A etapa atual não pode ser pulada.")
    const next = item.stageStates
      .filter((state) => state.stagePosition > current.stagePosition)
      .sort((first, second) => first.stagePosition - second.stagePosition)[0]
    const isFinalStage = !next && target.id === current.id
    if (!isFinalStage && target.id !== next?.id) throw new DomainError("INVALID_TRANSITION", "Só é possível pular para a próxima etapa aplicável.")
    const timestamp = new Date().toISOString(); const before = { itemId: item.id, stageId: current.stageId, status: current.status }
    current.status = "SKIPPED"; current.completedAt = timestamp; current.version += 1
    if (target.id === current.id && !item.stageStates.some((state) => state.stagePosition > current.stagePosition)) item.status = "COMPLETED"
    else { target.status = "IN_PROGRESS"; target.enteredAt = timestamp; target.version += 1 }
    run.changes.unshift({ id: `change-${crypto.randomUUID()}`, type: "ITEM_STAGE_SKIPPED", reason: input.reason.trim(), before, after: { itemId: item.id, stageId: current.stageId, status: "SKIPPED", targetStageId: target.stageId }, actor: demoCatalogActor.name, createdAt: timestamp })
    item.version += 1; recalculateDemoRun(run); return structuredClone(run)
  }

  async blockProductionItem(input: BlockItemInput) {
    const run = findProductionRun(input.runId); const item = run.items.find((candidate) => candidate.id === input.itemId)
    assertActiveDemoRun(run)
    if (!item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    const state = item.stageStates.find((candidate) => candidate.status === "IN_PROGRESS")
    if (!state || !input.reason.trim()) throw new DomainError("REASON_REQUIRED", "Informe uma justificativa.")
    state.status = "BLOCKED"; state.blockedReason = input.reason.trim(); state.blockedAt = new Date().toISOString(); item.status = "BLOCKED"; item.version += 1; recalculateDemoRun(run); return structuredClone(run)
  }

  async unblockProductionItem(input: Omit<BlockItemInput, "reason">) {
    const run = findProductionRun(input.runId); const item = run.items.find((candidate) => candidate.id === input.itemId)
    assertActiveDemoRun(run)
    if (!item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    const state = item.stageStates.find((candidate) => candidate.status === "BLOCKED")
    if (!state) throw new DomainError("BLOCKED_ITEM_REQUIRED", "O item não está bloqueado.")
    state.status = "IN_PROGRESS"; state.blockedReason = undefined; state.blockedAt = undefined; item.status = "ACTIVE"; item.version += 1; recalculateDemoRun(run); return structuredClone(run)
  }

  async toggleProductionChecklist(checklistId: string, expectedVersion: number) {
    const found = findChecklist(checklistId); assertActiveDemoRun(found.run); if (found.checklist.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O controle foi alterado.")
    found.checklist.status = found.checklist.status === "DONE" ? "PENDING" : "DONE"; found.checklist.version += 1; return structuredClone(found.run)
  }

  async toggleProductionGate(gateId: string, expectedVersion: number) {
    const run = demoProductionRuns.find((candidate) => candidate.gates.some((gate) => gate.id === gateId)); const gate = run?.gates.find((candidate) => candidate.id === gateId)
    if (!run || !gate) throw new DomainError("NOT_FOUND", "Item do checklist global não encontrado.")
    assertActiveDemoRun(run)
    if (gate.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item do checklist global foi alterado.")
    gate.status = gate.status === "DONE" ? "PENDING" : "DONE"; gate.version += 1; recalculateDemoRun(run); return structuredClone(run)
  }

  async assignProductionItemPrintShop(itemId: string, printShopId: string | undefined, expectedVersion: number) {
    const run = demoProductionRuns.find((candidate) => candidate.items.some((item) => item.id === itemId)); const item = run?.items.find((candidate) => candidate.id === itemId)
    if (!run || !item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    assertActiveDemoRun(run)
    if (item.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    item.printShop = printShopId ? structuredClone(demoPrintShops.find((shop) => shop.id === printShopId)) : undefined
    if (printShopId && !item.printShop) throw new DomainError("PRINT_SHOP_NOT_FOUND", "Gráfica não encontrada.")
    item.version += 1; return structuredClone(run)
  }

  async setProductionItemPrintDeliveries(itemId: string, deliveries: ProductionPrintDelivery[], expectedVersion: number) {
    const run = demoProductionRuns.find((candidate) => candidate.items.some((item) => item.id === itemId)); const item = run?.items.find((candidate) => candidate.id === itemId)
    if (!run || !item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    assertActiveDemoRun(run)
    if (item.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    const validDate = (value: string | null) => value === null || isIsoDate(value)
    const validCount = (value: number | null, min: number) => value === null || (Number.isInteger(value) && value >= min)
    if (deliveries.length > 50 || deliveries.some((delivery) => !validDate(delivery.estimatedDate ?? null) || !validDate(delivery.deliveryDate ?? null))) throw new DomainError("INVALID_PRINT_DELIVERIES", "Revise as entregas da gráfica.")
    if (deliveries.some((delivery) => !validCount(delivery.estimatedQuantity ?? null, 1) || !validCount(delivery.receivedQuantity ?? null, 0))) throw new DomainError("INVALID_QUANTITY", "Informe uma quantidade válida.")
    const normalized = deliveries.map((delivery) => ({ id: delivery.id || crypto.randomUUID(), estimatedDate: delivery.estimatedDate ?? null, estimatedQuantity: delivery.estimatedQuantity ?? null, deliveryDate: delivery.deliveryDate ?? null, receivedQuantity: delivery.receivedQuantity ?? null }))
    if (JSON.stringify(item.printDeliveries ?? []) === JSON.stringify(normalized)) return structuredClone(run)
    Object.assign(item, { printDeliveries: normalized, ...summarizePrintDeliveries(normalized) })
    item.version += 1; return structuredClone(run)
  }

  async setProductionItemPrintChecklist(itemId: string, key: PrintChecklistKey, done: boolean, expectedVersion: number) {
    const run = demoProductionRuns.find((candidate) => candidate.items.some((item) => item.id === itemId)); const item = run?.items.find((candidate) => candidate.id === itemId)
    if (!run || !item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    assertActiveDemoRun(run)
    if (item.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    const current = item.printChecklist ?? []
    if (current.includes(key) === done) return structuredClone(run)
    item.printChecklist = done ? [...current, key] : current.filter((entry) => entry !== key)
    item.version += 1; return structuredClone(run)
  }

  async setProductionItemQuoteUrl(itemId: string, quoteUrl: string | null, expectedVersion: number) {
    const run = demoProductionRuns.find((candidate) => candidate.items.some((item) => item.id === itemId)); const item = run?.items.find((candidate) => candidate.id === itemId)
    if (!run || !item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    assertActiveDemoRun(run)
    if (item.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado.")
    const normalized = quoteUrl?.trim() || null
    if (normalized && !/^https?:\/\/\S+$/i.test(normalized)) throw new DomainError("INVALID_QUOTE_URL", "Informe um link de orçamento válido, começando com http:// ou https://.")
    if ((item.quoteUrl ?? null) === normalized) return structuredClone(run)
    item.quoteUrl = normalized
    item.version += 1; return structuredClone(run)
  }

  async getItemTypeCatalog() { return structuredClone(demoCatalogItemTypes) }
  async getKitPresets() { return structuredClone(demoKitPresets) }
  async getPipelineCatalog() {
    return structuredClone(demoPipelineCatalog.map((template) => {
      const hasProductionRuns = demoProductionRuns.some((run) => run.pipeline.templateId === template.id || template.versions.some((version) => version.id === run.pipeline.id))
      return { ...template, hasProductionRuns, canDelete: !hasProductionRuns }
    }))
  }

  async uploadCatalogItemDocument(input: UploadCatalogItemDocumentInput) {
    const { product, item } = findCatalogItem(input.itemId)
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado por outra pessoa.")
    if (product.status === "IN_PRODUCTION" && !input.confirmActiveProduction) throw new DomainError("ACTIVE_PRODUCTION_CONFIRMATION_REQUIRED", "Produto em produção. Alterar mesmo assim?")
    const before = structuredClone(item); const timestamp = new Date().toISOString(); const sameKind = item.documents.filter((document) => document.kind === input.kind)
    sameKind.forEach((document) => { document.isCurrent = false; document.supersededAt = timestamp })
    const storagePath = `demo://${input.itemId}/${input.kind.toLowerCase()}/${crypto.randomUUID()}`; demoDocumentFiles.set(storagePath, input.file)
    item.documents.unshift({ id: `catalog-document-${crypto.randomUUID()}`, kind: input.kind, storagePath, originalFilename: input.file.name, mimeType: normalizedDocumentMime(input.file), sizeBytes: input.file.size, sha256: await fileSha256(input.file), version: Math.max(0, ...sameKind.map((document) => document.version)) + 1, isCurrent: true, createdAt: timestamp })
    item.version += 1
    recordCatalogItemActivity(item.id, { type: "CATALOG_DOCUMENT_REGISTERED", title: "Documento adicionado", createdAt: timestamp })
    if (product.status === "IN_PRODUCTION" && product.currentRunId) {
      const run = findProductionRun(product.currentRunId); const operational = run.items.find((candidate) => candidate.catalogItemId === item.id && candidate.status !== "CANCELLED")
      if (operational) { operational.catalogSnapshot = structuredClone(item); operational.version += 1 }
      run.changes.unshift({ id: `change-${crypto.randomUUID()}`, type: "CATALOG_DOCUMENT_REGISTERED", before, after: structuredClone(item), actor: "Marina Rocha", createdAt: timestamp })
      run.version += 1
    }
    return structuredClone(item)
  }

  async getCatalogItemDocumentUrl(document: CatalogItemDocument) {
    const file = demoDocumentFiles.get(document.storagePath)
    return file && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : document.storagePath
  }

  async deleteCatalogItemDocument(input: DeleteCatalogItemDocumentInput): Promise<DeleteCatalogItemDocumentResult> {
    const { product, item } = findCatalogItem(input.itemId)
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado por outra pessoa.")
    if (product.status === "IN_PRODUCTION" && !input.confirmActiveProduction) throw new DomainError("ACTIVE_PRODUCTION_CONFIRMATION_REQUIRED", "Produto em produção. Alterar mesmo assim?")
    const document = item.documents.find((candidate) => candidate.id === input.documentId)
    if (!document) throw new DomainError("DOCUMENT_NOT_FOUND", "Documento não encontrado.")
    const before = structuredClone(item)
    item.documents = item.documents.filter((candidate) => candidate.id !== document.id)
    if (document.isCurrent) {
      const replacement = item.documents
        .filter((candidate) => candidate.kind === document.kind)
        .sort((left, right) => right.version - left.version)[0]
      if (replacement) { replacement.isCurrent = true; replacement.supersededAt = undefined }
    }
    const storageRetainedForHistory = [...demoCatalogItemEditionSnapshots.values()].some((snapshot) =>
      snapshot.itemId === item.id && snapshot.snapshotData.documents.some((candidate) => candidate.id === document.id),
    )
    if (!storageRetainedForHistory) demoDocumentFiles.delete(document.storagePath)
    item.version += 1
    product.version += 1
    product.updatedAt = new Date().toISOString()
    recordCatalogItemActivity(item.id, { type: "CATALOG_DOCUMENT_DELETED", title: "Documento excluído" })
    if (product.status === "IN_PRODUCTION" && product.currentRunId) {
      const run = findProductionRun(product.currentRunId)
      const operational = run.items.find((candidate) => candidate.catalogItemId === item.id && candidate.status !== "CANCELLED")
      if (operational) { operational.catalogSnapshot = structuredClone(item); operational.version += 1 }
      run.changes.unshift({ id: `change-${crypto.randomUUID()}`, type: "CATALOG_DOCUMENT_DELETED", before, after: structuredClone(item), actor: "Marina Rocha", createdAt: new Date().toISOString() })
      run.version += 1
    }
    return { item: structuredClone(item), storageCleanupFailed: false, storageRetainedForHistory }
  }

  async uploadCatalogProductImage(input: UploadCatalogProductImageInput) {
    const product = findCatalogProduct(input.productId)
    if (product.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O produto foi alterado por outra pessoa.")
    if (input.kind !== "MOCKUP" || input.file.size <= 0 || input.file.size > 10_000_000 || !["image/png", "image/jpeg", "image/webp"].includes(input.file.type)) throw new DomainError("INVALID_IMAGE", "Use PNG, JPEG ou WebP de até 10 MB.")
    const storagePath = `demo://${product.id}/${input.kind.toLowerCase()}/${crypto.randomUUID()}`
    demoProductImageFiles.set(storagePath, input.file)
    product.images = [...(product.images ?? []), { id: `catalog-image-${crypto.randomUUID()}`, kind: input.kind, storagePath, originalFilename: input.file.name, mimeType: input.file.type, sizeBytes: input.file.size, createdAt: new Date().toISOString() }]
    product.version += 1; product.updatedAt = new Date().toISOString()
    return structuredClone(product)
  }

  async deleteCatalogProductImage(input: import("@/types/domain").DeleteCatalogProductImageInput): Promise<import("@/types/domain").DeleteCatalogProductImageResult> {
    const product = findCatalogProduct(input.productId)
    if (product.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O produto foi alterado por outra pessoa.")
    const image = product.images?.find((candidate) => candidate.id === input.imageId)
    if (!image) throw new DomainError("PRODUCT_IMAGE_NOT_FOUND", "Imagem não encontrada.")
    const publicEntry = product.publicMedia?.find((entry) => entry.imageId === image.id)
    if (product.publicPublishedAt && publicEntry && product.publicMedia?.length === 1) {
      throw new DomainError("PRODUCT_IMAGE_REQUIRED_FOR_PUBLICATION", "Retire a publicação antes de excluir a única imagem pública.")
    }
    demoProductImageFiles.delete(image.storagePath)
    product.images = (product.images ?? []).filter((candidate) => candidate.id !== image.id)
    product.publicMedia = (product.publicMedia ?? []).filter((entry) => entry.imageId !== image.id).map((entry, index) => ({ ...entry, position: index + 1, isPrimary: index === 0 }))
    product.version += 1; product.updatedAt = new Date().toISOString()
    return { product: structuredClone(product), storageCleanupFailed: false }
  }

  async uploadCatalogItemMockupAsset(input: UploadCatalogItemMockupAssetInput) {
    const { product, item } = findCatalogItem(input.itemId)
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado por outra pessoa.")
    if (product.status === "IN_PRODUCTION" && !input.confirmActiveProduction) throw new DomainError("ACTIVE_PRODUCTION_CONFIRMATION_REQUIRED", "Produto em produção. Alterar mesmo assim?")
    const definition = demoCatalogItemTypes.find((type) => type.id === item.itemType.id)?.mockupAssets?.find((asset) => asset.id === input.assetDefinitionId)
    if (!definition) throw new DomainError("INVALID_MOCKUP_ASSET", "Asset de mockup inválido.")
    const existing = item.mockupAssets?.find((asset) => asset.definitionId === definition.id)
    const currentTotal = (item.mockupAssets ?? []).reduce((total, asset) => total + asset.sizeBytes, 0)
    if (input.file.size <= 0 || !["image/png", "image/jpeg", "image/webp"].includes(input.file.type)) throw new DomainError("INVALID_IMAGE", "Use PNG, JPEG ou WebP.")
    if (currentTotal - (existing?.sizeBytes ?? 0) + input.file.size > 10_000_000) throw new DomainError("MOCKUP_ASSETS_TOTAL_LIMIT", "Os arquivos para mockup deste item devem somar no máximo 10 MB.")
    if (existing) demoItemMockupAssetFiles.delete(existing.storagePath)
    const storagePath = `demo://${item.id}/${definition.id}/${crypto.randomUUID()}`
    demoItemMockupAssetFiles.set(storagePath, input.file)
    const asset: CatalogItemMockupAsset = { id: existing?.id ?? `mockup-asset-${crypto.randomUUID()}`, definitionId: definition.id, name: definition.name, position: definition.position, storagePath, originalFilename: input.file.name, mimeType: input.file.type, sizeBytes: input.file.size, createdAt: new Date().toISOString() }
    item.mockupAssets = [...(item.mockupAssets ?? []).filter((candidate) => candidate.definitionId !== definition.id), asset].sort((left, right) => left.position - right.position)
    item.version += 1; product.version += 1; product.updatedAt = new Date().toISOString()
    recordCatalogItemActivity(item.id, { type: "CATALOG_ITEM_UPDATED", title: "Arquivo para mockup atualizado" })
    return structuredClone(item)
  }

  async deleteCatalogItemMockupAsset(input: DeleteCatalogItemMockupAssetInput): Promise<DeleteCatalogItemMockupAssetResult> {
    const { product, item } = findCatalogItem(input.itemId)
    if (item.version !== input.expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O item foi alterado por outra pessoa.")
    if (product.status === "IN_PRODUCTION" && !input.confirmActiveProduction) throw new DomainError("ACTIVE_PRODUCTION_CONFIRMATION_REQUIRED", "Produto em produção. Alterar mesmo assim?")
    const asset = item.mockupAssets?.find((candidate) => candidate.id === input.assetId)
    if (!asset) throw new DomainError("MOCKUP_ASSET_NOT_FOUND", "Arquivo para mockup não encontrado.")
    item.mockupAssets = (item.mockupAssets ?? []).filter((candidate) => candidate.id !== asset.id)
    demoItemMockupAssetFiles.delete(asset.storagePath)
    item.version += 1; product.version += 1; product.updatedAt = new Date().toISOString()
    recordCatalogItemActivity(item.id, { type: "CATALOG_ITEM_UPDATED", title: "Arquivo para mockup excluído" })
    return { item: structuredClone(item), storageCleanupFailed: false }
  }

  async getCatalogItemMockupAssetUrl(asset: CatalogItemMockupAsset) {
    const file = demoItemMockupAssetFiles.get(asset.storagePath)
    return file && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : asset.storagePath
  }

  async getCatalogProductImageUrl(image: import("@/types/domain").CatalogProductImage) {
    const file = demoProductImageFiles.get(image.storagePath)
    return file && typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : image.storagePath
  }

  async getMockupStudioKit(): Promise<unknown> {
    throw new DomainError("UNAVAILABLE_IN_DEMO", "Os mockups padrão só podem ser gerados com o Supabase conectado.")
  }

  async stageCatalogProductImages(): Promise<import("@/types/domain").StagedCatalogProductImage[]> {
    throw new DomainError("UNAVAILABLE_IN_DEMO", "Os mockups padrão só podem ser gerados com o Supabase conectado.")
  }

  async replaceStudioKitImages(): Promise<import("@/types/domain").ReplaceStudioKitImagesResult> {
    throw new DomainError("UNAVAILABLE_IN_DEMO", "Os mockups padrão só podem ser gerados com o Supabase conectado.")
  }

  async discardStagedCatalogProductImages() {}

  async getCatalogProductImagePreview(image: import("@/types/domain").CatalogProductImage) {
    return { url: await this.getCatalogProductImageUrl(image), isThumbnail: false }
  }

  async saveCatalogPublicMedia(productId: string, media: CatalogPublicMediaSelection[], expectedVersion: number, altTexts: CatalogImageAltText[] = [], { onProgress }: SaveCatalogPublicMediaOptions = {}) {
    onProgress?.({ step: "SAVING" })
    const product = findCatalogProduct(productId)
    if (product.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O produto foi alterado por outra pessoa.")
    if (media.length && media.filter((entry) => entry.isPrimary).length !== 1) throw new DomainError("INVALID_PUBLIC_MEDIA", "Defina uma imagem principal.")
    product.publicMedia = media.map((entry) => ({ ...entry, id: `public-media-${crypto.randomUUID()}` }))
    const savedAltTexts = new Map([...altTexts.map((entry) => [entry.imageId, entry.altText.trim() || undefined] as const), ...media.map((entry) => [entry.imageId, entry.altText.trim()] as const)])
    product.images = product.images?.map((image) => savedAltTexts.has(image.id) ? { ...image, altText: savedAltTexts.get(image.id) } : image)
    product.version += 1; product.updatedAt = new Date().toISOString()
    return structuredClone(product)
  }

  async publishCatalogProduct(productId: string, publicSlug: string, expectedVersion: number) {
    const product = findCatalogProduct(productId)
    if (product.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O produto foi alterado por outra pessoa.")
    if (!product.publicMedia?.length) throw new DomainError("PUBLIC_MEDIA_REQUIRED", "Selecione uma imagem pública.")
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(publicSlug)) throw new DomainError("INVALID_PUBLIC_SLUG", "Use identificador-do-selo/titulo-do-produto.")
    if (product.publicSlug && product.publicSlug !== publicSlug) throw new DomainError("PUBLIC_SLUG_LOCKED", "O slug público não pode ser alterado.")
    product.publicSlug = publicSlug; product.publicPublishedAt ??= new Date().toISOString(); product.version += 1; product.updatedAt = new Date().toISOString()
    return structuredClone(product)
  }

  async unpublishCatalogProduct(productId: string, expectedVersion: number) {
    const product = findCatalogProduct(productId)
    if (product.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O produto foi alterado por outra pessoa.")
    product.publicPublishedAt = undefined; product.publicMedia = []; product.version += 1; product.updatedAt = new Date().toISOString()
    return structuredClone(product)
  }

  async getDashboard(): Promise<DashboardData> {
    await wait()
    const products = demoProducts.map(summary)
    const inProgress = products.filter((product) => product.status === "ACTIVE")
    const productionStages = Array.from(new Map(demoProductionRuns.flatMap((run) => run.stages).map((stage) => [stage.id, stage])).values())
    const bottlenecks = productionStages.map((stage) => {
      const states = demoProductionRuns.flatMap((run) => run.items.flatMap((item) => item.stageStates)).filter((state) => state.stageId === stage.id && (state.status === "IN_PROGRESS" || state.status === "BLOCKED"))
      const aging = states.map((state) => state.enteredAt ? Math.floor((Date.now() - new Date(state.enteredAt).getTime()) / 86_400_000) : 0).sort((a, b) => a - b)
      const middle = Math.floor(aging.length / 2)
      return {
        stageId: stage.id,
        stageName: stage.name,
        activeItems: states.length,
        medianAgingDays: aging.length === 0 ? 0 : aging.length % 2 ? aging[middle] : Math.round((aging[middle - 1] + aging[middle]) / 2),
        blockedItems: states.filter((state) => state.status === "BLOCKED").length,
      }
    }).filter((stage) => stage.activeItems > 0)

    return {
      activeProducts: inProgress.length,
      blockedProducts: products.filter((product) => product.status === "BLOCKED").length,
      completedThisMonth: demoProductionRuns.filter((run) => run.status === "COMPLETED" && run.completedAt?.startsWith(new Date().toISOString().slice(0, 7))).length,
      criticalAgingDays: Math.max(...demoProductionRuns.map((run) => productionSummary(run).criticalAgingDays), 0),
      products,
      bottlenecks,
      activeProductions: demoProductionRuns.filter((run) => run.status === "ACTIVE").length,
      blockedProductions: demoProductionRuns.filter((run) => run.status === "STUCK").length,
      catalogProducts: demoCatalogProducts.length,
      imprintCount: demoTemplateCatalog.imprints.length,
      recentCatalogProducts: demoCatalogProducts
        .map(catalogSummary)
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
        .slice(0, 10),
      productions: demoProductionRuns.map(productionSummary),
    }
  }

  async listNotifications() {
    return []
  }

  async markNotificationRead(_notificationId: string) {}

  async getTemplateCatalog() {
    await wait(80)
    return structuredClone({
      ...demoTemplateCatalog,
      kitTemplates: demoTemplateCatalog.kitTemplates.map((template) => ({ ...template, revision: demoEditVersion(template.id) })),
      pipelineTemplates: demoTemplateCatalog.pipelineTemplates.map((template) => ({
        ...template,
        hasProductionRuns: demoProductionRuns.some((run) => run.pipeline.templateId === template.id || template.versions.some((version) => version.id === run.pipeline.id)),
        canDelete: !demoProductionRuns.some((run) => run.pipeline.templateId === template.id || template.versions.some((version) => version.id === run.pipeline.id)),
        versions: template.versions.map((version) => ({
          ...version,
          revision: demoEditVersion(version.id),
          editable: !demoProductionRuns.some((run) => run.pipeline.id === version.id),
        })),
      })),
    })
  }

  async createItemType(input: ItemTypeInput) {
    await wait()
    const code = input.code?.trim().toUpperCase() || crypto.randomUUID().toUpperCase()
    const name = input.name.trim()
    if (!/^[A-Z0-9][A-Z0-9_-]{1,35}$/.test(code) || name.length < 2 || name.length > 100) {
      throw new DomainError("VALIDATION_ERROR", "Informe código e nome válidos.")
    }
    if (demoTemplateCatalog.itemTypes.some((type) => type.code.toLowerCase() === code.toLowerCase() || type.name.toLocaleLowerCase("pt-BR") === name.toLocaleLowerCase("pt-BR"))) {
      throw new DomainError("DUPLICATE_ITEM_TYPE", "Já existe um tipo com esse código ou nome.")
    }
    const id = crypto.randomUUID()
    demoTemplateCatalog.itemTypes.push({ id, code, name })
    return id
  }

  async updateItemType(itemTypeId: string, nameInput: string) {
    await wait()
    const itemType = demoTemplateCatalog.itemTypes.find((candidate) => candidate.id === itemTypeId)
    const name = nameInput.trim()
    if (!itemType) throw new DomainError("NOT_FOUND", "Tipo de item não encontrado.")
    if (name.length < 2 || name.length > 100) throw new DomainError("VALIDATION_ERROR", "Informe um nome válido.")
    if (demoTemplateCatalog.itemTypes.some((candidate) => candidate.id !== itemTypeId && candidate.name.toLocaleLowerCase("pt-BR") === name.toLocaleLowerCase("pt-BR"))) {
      throw new DomainError("DUPLICATE_ITEM_TYPE", "Já existe um tipo com esse nome.")
    }
    itemType.name = name
    demoTemplateCatalog.kitTemplates.flatMap((template) => template.items)
      .filter((item) => item.itemTypeId === itemTypeId).forEach((item) => { item.itemTypeName = name })
    demoProducts.flatMap((product) => product.items).filter((item) => item.type === itemType.code)
      .forEach((item) => { item.typeLabel = name })
  }

  async saveItemType(input: ItemTypeInput, itemTypeId?: string, expectedVersion?: number) {
    await wait()
    const existing = itemTypeId ? demoCatalogItemTypes.find((candidate) => candidate.id === itemTypeId) : undefined
    if (itemTypeId && !existing) throw new DomainError("NOT_FOUND", "Tipo de item não encontrado.")
    if (existing && expectedVersion !== undefined && existing.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O tipo foi alterado por outra pessoa.")
    const code = existing?.code ?? input.code?.trim().toUpperCase() ?? crypto.randomUUID().toUpperCase(); const name = input.name.trim()
    if (!/^[A-Z0-9][A-Z0-9_-]{1,35}$/.test(code) || name.length < 2) throw new DomainError("VALIDATION_ERROR", "Informe código e nome válidos.")
    if (demoCatalogItemTypes.some((candidate) => candidate.id !== itemTypeId && (candidate.code === code || candidate.name.toLocaleLowerCase("pt-BR") === name.toLocaleLowerCase("pt-BR")))) throw new DomainError("DUPLICATE_ITEM_TYPE", "Já existe um tipo com esse código ou nome.")
    const id = existing?.id ?? crypto.randomUUID()
    const fields = (input.fields ?? []).map((field, index) => { const current = existing?.fields.find((candidate) => candidate.id === field.id); return { ...field, id: field.id ?? crypto.randomUUID(), code: current?.code ?? field.code?.trim().toLowerCase() ?? crypto.randomUUID(), label: field.label.trim(), position: index + 1, version: current?.version ?? 1 } })
    const mockupAssets = (input.mockupAssets ?? []).map((asset, index) => { const current = existing?.mockupAssets?.find((candidate) => candidate.id === asset.id); return { id: current?.id ?? crypto.randomUUID(), name: asset.name.trim(), position: index + 1, version: (current?.version ?? 0) + 1 } })
    if (mockupAssets.some((asset) => !asset.name) || new Set(mockupAssets.map((asset) => asset.name.toLocaleLowerCase("pt-BR"))).size !== mockupAssets.length) throw new DomainError("VALIDATION_ERROR", "Informe nomes diferentes para os assets de mockup.")
    const removedAssetIds = (existing?.mockupAssets ?? []).filter((asset) => !mockupAssets.some((candidate) => candidate.id === asset.id)).map((asset) => asset.id)
    if (removedAssetIds.some((assetId) => demoCatalogProducts.some((product) => product.items.some((item) => item.mockupAssets?.some((asset) => asset.definitionId === assetId))))) throw new DomainError("MOCKUP_ASSET_IN_USE", "Este asset já possui arquivos em itens e não pode ser removido.")
    if (existing) Object.assign(existing, { name, isBook: input.isBook ?? existing.isBook, fields, mockupAssets, version: existing.version + 1 })
    else demoCatalogItemTypes.push({ id, code, name, isBook: input.isBook ?? false, fields, mockupAssets, variants: [], version: 1 })
    const legacy = demoTemplateCatalog.itemTypes.find((candidate) => candidate.id === id)
    if (legacy) legacy.name = name; else demoTemplateCatalog.itemTypes.push({ id, code, name })
    return id
  }

  async deleteItemType(itemTypeId: string, expectedVersion: number) {
    await wait()
    const index = demoCatalogItemTypes.findIndex((candidate) => candidate.id === itemTypeId)
    const itemType = demoCatalogItemTypes[index]
    if (!itemType) throw new DomainError("NOT_FOUND", "Tipo de item não encontrado.")
    if (itemType.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "O tipo foi alterado por outra pessoa.")
    const inUse = demoCatalogProducts.some((product) => product.items.some((item) => item.itemType.id === itemTypeId && !item.archivedAt))
      || demoKitPresets.some((preset) => preset.items.some((item) => item.itemTypeId === itemTypeId))
      || demoProductionRuns.some((run) => run.items.some((item) => item.itemType.id === itemTypeId))
      || demoPipelineCatalog.some((pipeline) => pipeline.versions.some((version) => version.stages.some((stage) => stage.applicableItemTypeIds.includes(itemTypeId) || stage.checklists.some((item) => item.itemTypeIds.includes(itemTypeId)))))
    if (inUse) throw new DomainError("ITEM_TYPE_IN_USE", "Este tipo está em uso e não pode ser apagado.")
    demoCatalogProducts.forEach((product) => {
      product.items = product.items.filter((item) => item.itemType.id !== itemTypeId || !item.archivedAt)
    })
    demoCatalogItemTypes.splice(index, 1)
    const legacyIndex = demoTemplateCatalog.itemTypes.findIndex((candidate) => candidate.id === itemTypeId)
    if (legacyIndex >= 0) demoTemplateCatalog.itemTypes.splice(legacyIndex, 1)
  }

  async saveItemTypeVariant(itemTypeId: string, input: ItemTypeVariantInput, variantId?: string, expectedVersion?: number) {
    await wait()
    const itemType = demoCatalogItemTypes.find((candidate) => candidate.id === itemTypeId)
    if (!itemType) throw new DomainError("NOT_FOUND", "Tipo de item não encontrado.")
    const existing = variantId ? itemType.variants.find((candidate) => candidate.id === variantId) : undefined
    if (variantId && !existing) throw new DomainError("NOT_FOUND", "Variante não encontrada.")
    if (existing && expectedVersion !== undefined && existing.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A variante foi alterada por outra pessoa.")
    const externalId = existing?.externalId ?? input.externalId?.trim().toLowerCase() ?? crypto.randomUUID(); const name = input.name.trim()
    if (!/^[a-z0-9][a-z0-9_-]{1,99}$/.test(externalId) || name.length < 2) throw new DomainError("VALIDATION_ERROR", "Informe identificador e nome válidos.")
    if (itemType.variants.some((candidate) => candidate.id !== variantId && candidate.externalId === externalId)) throw new DomainError("DUPLICATE_ITEM_TYPE_VARIANT", "Já existe uma variante com esse identificador.")
    const fieldValues = input.fieldValues.flatMap(({ fieldId, value }) => {
      const field = itemType.fields.find((candidate) => candidate.id === fieldId)
      return field && value !== "" && value !== null && value !== undefined ? [{ fieldId, code: field.code, label: field.label, type: field.type, unit: field.unit, value }] : []
    })
    const variant = { id: existing?.id ?? crypto.randomUUID(), externalSource: existing?.externalSource ?? "barca-codex", externalId, name, sourceHash: crypto.randomUUID(), metadata: existing?.metadata ?? {}, version: (existing?.version ?? 0) + 1, fieldValues }
    if (existing) Object.assign(existing, variant); else itemType.variants.push(variant)
    return variant.id
  }

  async deleteItemTypeVariant(variantId: string, expectedVersion: number) {
    await wait()
    const itemType = demoCatalogItemTypes.find((candidate) => candidate.variants.some((variant) => variant.id === variantId))
    const index = itemType?.variants.findIndex((variant) => variant.id === variantId) ?? -1
    const variant = itemType?.variants[index]
    if (!itemType || !variant) throw new DomainError("NOT_FOUND", "Variante não encontrada.")
    if (variant.version !== expectedVersion) throw new DomainError("CONCURRENCY_CONFLICT", "A variante foi alterada por outra pessoa.")
    const inUse = demoCatalogProducts.some((product) => product.items.some((item) => item.variant?.id === variantId))
      || demoKitPresets.some((preset) => preset.items.some((item) => item.variantId === variantId))
    if (inUse) throw new DomainError("ITEM_TYPE_VARIANT_IN_USE", "Esta variante está em uso e não pode ser apagada.")
    itemType.variants.splice(index, 1)
  }

  async createKitTemplate(input: CreateKitTemplateInput) {
    await wait()
    const selectedImprint = demoTemplateCatalog.imprints.find((imprint) => imprint.id === input.imprintId)
    if (!input.name.trim() || !selectedImprint) {
      throw new DomainError("VALIDATION_ERROR", "Selo e nome são obrigatórios.")
    }
    if (!selectedImprint.active) throw new DomainError("IMPRINT_INACTIVE", "Ative o selo antes de criar modelos.")
    if (demoTemplateCatalog.kitTemplates.some((template) => template.imprintId === input.imprintId && template.name.toLowerCase() === input.name.trim().toLowerCase())) {
      throw new DomainError("VALIDATION_ERROR", "Já existe um modelo com esse nome no selo.")
    }
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === input.imprintId)!
    const templateId = `kit-${crypto.randomUUID()}`
    const template = {
      id: templateId,
      name: input.name.trim(),
      imprintId: imprint.id,
      imprintName: imprint.name,
      items: kitItems(input.items),
    }
    demoTemplateCatalog.kitTemplates.push(template)
    demoKitPresets.push(structuredClone(template))
    return templateId
  }

  async updateKitTemplate(templateId: string, input: CreateKitTemplateInput, expectedVersion?: number) {
    await wait()
    const template = demoTemplateCatalog.kitTemplates.find((candidate) => candidate.id === templateId)
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === input.imprintId)
    if (!template) throw new DomainError("KIT_TEMPLATE_NOT_FOUND", "Modelo de kit não encontrado.")
    if (expectedVersion !== undefined && expectedVersion !== demoEditVersion(templateId)) throw new DomainError("CONCURRENCY_CONFLICT", "O modelo foi alterado por outra pessoa.")
    if (!input.name.trim() || !imprint) throw new DomainError("VALIDATION_ERROR", "Selo e nome são obrigatórios.")
    if (!imprint.active) throw new DomainError("IMPRINT_INACTIVE", "Ative o selo antes de editar modelos.")
    if (demoTemplateCatalog.kitTemplates.some((candidate) => candidate.id !== templateId && candidate.imprintId === input.imprintId && candidate.name.toLowerCase() === input.name.trim().toLowerCase())) {
      throw new DomainError("VALIDATION_ERROR", "Já existe um modelo com esse nome no selo.")
    }
    template.name = input.name.trim()
    template.imprintId = imprint.id
    template.imprintName = imprint.name
    template.items = kitItems(input.items)
    bumpDemoEditVersion(templateId)
    const preset = demoKitPresets.find((candidate) => candidate.id === templateId)
    if (preset) Object.assign(preset, structuredClone(template))
    return template.id
  }

  async deleteKitTemplate(templateId: string) {
    await wait()
    const index = demoTemplateCatalog.kitTemplates.findIndex((candidate) => candidate.id === templateId)
    if (index < 0) throw new DomainError("KIT_TEMPLATE_NOT_FOUND", "Modelo de kit não encontrado.")
    demoTemplateCatalog.kitTemplates.splice(index, 1)
    const presetIndex = demoKitPresets.findIndex((candidate) => candidate.id === templateId)
    if (presetIndex >= 0) demoKitPresets.splice(presetIndex, 1)
  }

  async createPipelineTemplate(input: CreatePipelineTemplateInput) {
    await wait()
    const selectedImprint = demoTemplateCatalog.imprints.find((imprint) => imprint.id === input.imprintId)
    if (!input.name.trim() || !selectedImprint) {
      throw new DomainError("VALIDATION_ERROR", "Selo e nome são obrigatórios.")
    }
    if (!selectedImprint.active) throw new DomainError("IMPRINT_INACTIVE", "Ative o selo antes de criar modelos.")
    if (demoTemplateCatalog.pipelineTemplates.some((template) => template.imprintId === input.imprintId && template.name.toLowerCase() === input.name.trim().toLowerCase())) {
      throw new DomainError("VALIDATION_ERROR", "Já existe um fluxo de trabalho com esse nome no selo.")
    }
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === input.imprintId)!
    const versionId = `pipeline-version-${crypto.randomUUID()}`
    demoTemplateCatalog.pipelineTemplates.push({
      id: `pipeline-${crypto.randomUUID()}`,
      name: input.name.trim(),
      imprintId: imprint.id,
      imprintName: imprint.name,
      active: true,
      hasProductionRuns: false,
      canDelete: true,
      versions: [{ id: versionId, version: 1, createdAt: new Date().toISOString(), ...pipelineContent(input) }],
    })
    const template = demoTemplateCatalog.pipelineTemplates.at(-1)!
    demoPipelineCatalog.push({ id: template.id, name: template.name, active: true, hasProductionRuns: false, canDelete: true, versions: structuredClone(template.versions) })
    return versionId
  }

  async updatePipelineTemplateVersion(versionId: string, input: PipelineVersionContentInput, expectedVersion?: number) {
    await wait()
    const { version } = findPipelineVersion(versionId)
    if (expectedVersion !== undefined && expectedVersion !== demoEditVersion(versionId)) throw new DomainError("CONCURRENCY_CONFLICT", "O fluxo de trabalho foi alterado por outra pessoa.")
    if (version.publishedAt && demoProductionRuns.some((run) => run.pipeline.id === version.id)) {
      throw new DomainError("PIPELINE_VERSION_IN_USE", "Esta versão já foi usada por uma produção. Crie uma nova versão para preservar o histórico.")
    }
    Object.assign(version, pipelineContent(input))
    const catalogVersion = demoPipelineCatalog.flatMap((template) => template.versions).find((candidate) => candidate.id === versionId)
    if (catalogVersion) Object.assign(catalogVersion, structuredClone(version))
    bumpDemoEditVersion(versionId)
  }

  async createPipelineTemplateVersion(sourceVersionId: string) {
    await wait()
    const { template, version: source } = findPipelineVersion(sourceVersionId)
    if (template.versions.some((version) => !version.publishedAt)) throw new DomainError("DRAFT_ALREADY_EXISTS", "Já existe um rascunho.")
    const id = `pipeline-version-${crypto.randomUUID()}`
    template.versions.unshift({
      id,
      version: Math.max(...template.versions.map((version) => version.version)) + 1,
      createdAt: new Date().toISOString(),
      ...pipelineContent({
        stages: source.stages.map((stage) => ({
          name: stage.name,
          shortName: stage.shortName,
          editorialFunction: stage.editorialFunction ?? null,
          usesPreviousDriveFolder: stage.usesPreviousDriveFolder ?? false,
          applicableItemTypeIds: stage.applicableItemTypeIds,
          checklists: stage.checklists.map((check) => ({ label: check.label, itemTypeIds: check.itemTypeIds })),
        })),
        gates: source.gates.map((gate) => ({ label: gate.label })),
      }),
    })
    const catalogTemplate = demoPipelineCatalog.find((candidate) => candidate.id === template.id)
    if (catalogTemplate) catalogTemplate.versions.unshift(structuredClone(template.versions[0]))
    return id
  }

  async publishPipelineTemplateVersion(versionId: string) {
    await wait()
    const { version } = findPipelineVersion(versionId)
    if (version.publishedAt) throw new DomainError("DRAFT_REQUIRED", "A versão já foi publicada.")
    version.publishedAt = new Date().toISOString()
    const catalogVersion = demoPipelineCatalog.flatMap((template) => template.versions).find((candidate) => candidate.id === versionId)
    if (catalogVersion) catalogVersion.publishedAt = version.publishedAt
  }

  async setPipelineTemplateActive(templateId: string, active: boolean) {
    await wait()
    const template = demoTemplateCatalog.pipelineTemplates.find((candidate) => candidate.id === templateId)
    if (!template) throw new DomainError("NOT_FOUND", "Fluxo de trabalho não encontrado.")
    template.active = active
    const catalogTemplate = demoPipelineCatalog.find((candidate) => candidate.id === templateId)
    if (catalogTemplate) catalogTemplate.active = active
  }

  async setPipelineStageFunction(stageId: string, editorialFunction: EditorialFunction | null) {
    await wait()
    const stages = [...demoTemplateCatalog.pipelineTemplates, ...demoPipelineCatalog].flatMap((template) => template.versions.flatMap((version) => version.stages)).filter((stage) => stage.id === stageId)
    if (!stages.length) throw new DomainError("NOT_FOUND", "Etapa não encontrada.")
    for (const stage of stages) stage.editorialFunction = editorialFunction
  }

  async deletePipelineTemplate(templateId: string) {
    await wait()
    const index = demoTemplateCatalog.pipelineTemplates.findIndex((candidate) => candidate.id === templateId)
    const template = demoTemplateCatalog.pipelineTemplates[index]
    if (!template) throw new DomainError("NOT_FOUND", "Fluxo de trabalho não encontrado.")
    const inUse = demoProductionRuns.some((run) => run.pipeline.templateId === templateId || template.versions.some((version) => version.id === run.pipeline.id))
    if (inUse) throw new DomainError("PIPELINE_IN_USE", "Este fluxo de trabalho já foi usado em uma produção e não pode ser apagado.")
    demoTemplateCatalog.pipelineTemplates.splice(index, 1)
    const catalogIndex = demoPipelineCatalog.findIndex((candidate) => candidate.id === templateId)
    if (catalogIndex >= 0) demoPipelineCatalog.splice(catalogIndex, 1)
  }

  async listPipelineStageEstimates(pipelineTemplateId: string) {
    await wait(60)
    return { revision: demoEditVersion(`estimates:${pipelineTemplateId}`), estimates: structuredClone(demoPipelineStageEstimates.get(pipelineTemplateId) ?? []) }
  }

  async savePipelineStageEstimates(input: import("@/types/domain").SavePipelineStageEstimatesInput) {
    await wait()
    if (!demoTemplateCatalog.pipelineTemplates.some((template) => template.id === input.pipelineTemplateId)) throw new DomainError("NOT_FOUND", "Fluxo de trabalho não encontrado.")
    if (input.expectedVersion !== undefined && input.expectedVersion !== demoEditVersion(`estimates:${input.pipelineTemplateId}`)) throw new DomainError("CONCURRENCY_CONFLICT", "As estimativas foram alteradas por outra pessoa.")
    const stageKey = (name: string) => name.trim().toLocaleLowerCase("pt-BR")
    const stageKeys = new Set(input.stageNames.map(stageKey))
    if (input.estimates.some((estimate) => !stageKeys.has(stageKey(estimate.stageName)) || !(estimate.days > 0 && estimate.days <= 999))) throw new DomainError("INVALID_ESTIMATES", "Estimativas inválidas.")
    if (input.estimates.some((estimate) => !demoTemplateCatalog.imprints.some((imprint) => imprint.id === estimate.imprintId))) throw new DomainError("INVALID_IMPRINT", "Selo inválido.")
    const preserved = (demoPipelineStageEstimates.get(input.pipelineTemplateId) ?? []).filter((estimate) => !stageKeys.has(stageKey(estimate.stageName)))
    demoPipelineStageEstimates.set(input.pipelineTemplateId, [...preserved, ...input.estimates.map((estimate) => ({ ...estimate, stageName: estimate.stageName.trim(), days: Math.round(estimate.days * 100) / 100 }))])
    bumpDemoEditVersion(`estimates:${input.pipelineTemplateId}`)
  }

  async listImprints(): Promise<ImprintSummary[]> {
    await wait(60)
    return demoTemplateCatalog.imprints.map((imprint) => ({
      ...imprint,
      version: demoEditVersion(imprint.id),
      createdAt: "2026-06-01T12:00:00.000Z",
      updatedAt: "2026-06-01T12:00:00.000Z",
      productCount: demoCatalogProducts.filter((product) => product.imprint.id === imprint.id).length,
      productionProductCount: new Set(demoProductionRuns.filter((run) => run.status === "ACTIVE" || run.status === "STUCK").filter((run) => demoCatalogProducts.find((product) => product.id === run.product.id)?.imprint.id === imprint.id).map((run) => run.product.id)).size,
      logos: [...(demoImprintLogos.get(imprint.id) ?? [])],
    }))
  }

  async createImprint(input: ImprintInput) {
    await wait()
    const code = input.code.trim().toLowerCase()
    const skuSuffix = input.skuSuffix.trim().toUpperCase()
    const name = input.name.trim()
    const niche = input.niche?.trim() || undefined
    const tagline = input.tagline?.trim() || undefined
    const description = input.description?.trim() || undefined
    const instagramHandle = input.instagramHandle?.trim().replace(/^@/, "").toLowerCase() || undefined
    const websiteUrl = normalizeWebsiteUrl(input.websiteUrl)
    const country = input.country?.trim() || "Brasil"
    const backgroundColor = input.backgroundColor?.trim().toUpperCase() || undefined
    const textColor = input.textColor?.trim().toUpperCase() || undefined
    const driveRootFolderUrl = normalizeDriveFolderUrl(input.driveRootFolderUrl)
    if (!/^[a-z0-9][a-z0-9_-]{1,63}$/.test(code) || !/^[A-Z0-9]{2,24}$/.test(skuSuffix) || name.length < 2 || name.length > 100 || (niche?.length ?? 0) > 100 || (tagline?.length ?? 0) > 300 || (description?.length ?? 0) > 2000 || (instagramHandle && !/^[a-z0-9._]{1,30}$/.test(instagramHandle)) || (input.websiteUrl?.trim() && !websiteUrl) || country.length > 100 || (backgroundColor && !/^#[0-9A-F]{6}$/.test(backgroundColor)) || (textColor && !/^#[0-9A-F]{6}$/.test(textColor)) || (input.driveRootFolderUrl?.trim() && !driveRootFolderUrl)) {
      throw new DomainError("VALIDATION_ERROR", "Informe identificador, ID SKU e nome válidos.")
    }
    if (demoTemplateCatalog.imprints.some((imprint) => imprint.code === code || imprint.skuSuffix === skuSuffix || imprint.name.toLowerCase() === name.toLowerCase())) {
      throw new DomainError("DUPLICATE_IMPRINT", "Já existe um selo com esse identificador, ID SKU ou nome.")
    }
    const launch = demoImprintLaunch(input)
    const id = `imprint-${crypto.randomUUID()}`
    demoTemplateCatalog.imprints.push({ id, code, skuSuffix, name, niche, tagline, description, instagramHandle, websiteUrl, country, backgroundColor, textColor, driveRootFolderUrl, launchMonth: launch?.launchMonth ?? null, launchYear: launch?.launchYear ?? null, kind: input.kind ?? "BARCA", active: true })
    return id
  }

  async updateImprint(imprintId: string, input: ImprintInput, expectedVersion?: number) {
    await wait()
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === imprintId)
    if (!imprint) throw new DomainError("NOT_FOUND", "Selo não encontrado.")
    if (expectedVersion !== undefined && expectedVersion !== demoEditVersion(imprintId)) throw new DomainError("CONCURRENCY_CONFLICT", "O selo foi alterado por outra pessoa.")
    const code = input.code.trim().toLowerCase()
    const skuSuffix = input.skuSuffix.trim().toUpperCase()
    const name = input.name.trim()
    const niche = input.niche?.trim() || undefined
    const tagline = input.tagline?.trim() || undefined
    const description = input.description?.trim() || undefined
    const instagramHandle = input.instagramHandle?.trim().replace(/^@/, "").toLowerCase() || undefined
    const websiteUrl = normalizeWebsiteUrl(input.websiteUrl)
    const country = input.country?.trim() || imprint.country
    const backgroundColor = input.backgroundColor?.trim().toUpperCase() || undefined
    const textColor = input.textColor?.trim().toUpperCase() || undefined
    const driveRootFolderUrl = normalizeDriveFolderUrl(input.driveRootFolderUrl)
    if (!/^[a-z0-9][a-z0-9_-]{1,63}$/.test(code) || !/^[A-Z0-9]{2,24}$/.test(skuSuffix) || name.length < 2 || name.length > 100 || (niche?.length ?? 0) > 100 || (tagline?.length ?? 0) > 300 || (description?.length ?? 0) > 2000 || (instagramHandle && !/^[a-z0-9._]{1,30}$/.test(instagramHandle)) || (input.websiteUrl?.trim() && !websiteUrl) || !country || country.length > 100 || (backgroundColor && !/^#[0-9A-F]{6}$/.test(backgroundColor)) || (textColor && !/^#[0-9A-F]{6}$/.test(textColor)) || (input.driveRootFolderUrl?.trim() && !driveRootFolderUrl)) {
      throw new DomainError("VALIDATION_ERROR", "Informe identificador, ID SKU e nome válidos.")
    }
    if (demoTemplateCatalog.imprints.some((candidate) => candidate.id !== imprintId && (candidate.code === code || candidate.skuSuffix === skuSuffix || candidate.name.toLowerCase() === name.toLowerCase()))) {
      throw new DomainError("DUPLICATE_IMPRINT", "Já existe um selo com esse identificador, ID SKU ou nome.")
    }
    const launch = demoImprintLaunch(input)
    const previousName = imprint.name
    if (launch !== undefined) { imprint.launchMonth = launch?.launchMonth ?? null; imprint.launchYear = launch?.launchYear ?? null }
    if (input.kind) imprint.kind = input.kind
    imprint.code = code
    imprint.skuSuffix = skuSuffix
    imprint.name = name
    imprint.niche = niche
    imprint.tagline = tagline
    imprint.description = description
    imprint.instagramHandle = instagramHandle
    imprint.websiteUrl = websiteUrl
    imprint.country = country
    imprint.backgroundColor = backgroundColor
    imprint.textColor = textColor
    if (input.driveRootFolderUrl !== undefined) imprint.driveRootFolderUrl = driveRootFolderUrl
    demoTemplateCatalog.kitTemplates.filter((template) => template.imprintId === imprintId).forEach((template) => { template.imprintName = name })
    demoTemplateCatalog.pipelineTemplates.filter((template) => template.imprintId === imprintId).forEach((template) => { template.imprintName = name })
    demoProducts.filter((product) => product.imprint === previousName).forEach((product) => { product.imprint = name })
    bumpDemoEditVersion(imprintId)
  }

  async setImprintActive(imprintId: string, active: boolean, reason: string) {
    await wait()
    if (!reason.trim()) throw new DomainError("REASON_REQUIRED", "Informe o motivo da alteração.")
    const imprint = demoTemplateCatalog.imprints.find((candidate) => candidate.id === imprintId)
    if (!imprint) throw new DomainError("NOT_FOUND", "Selo não encontrado.")
    imprint.active = active
    bumpDemoEditVersion(imprintId)
  }

  async uploadImprintLogo(input: UploadImprintLogoInput) {
    await wait()
    if (!demoTemplateCatalog.imprints.some((imprint) => imprint.id === input.imprintId)) throw new DomainError("NOT_FOUND", "Selo não encontrado.")
    const fileError = imprintLogoFileError(input.format, input.file)
    if (fileError) throw new DomainError("INVALID_IMPRINT_LOGO", fileError)
    const logos = (demoImprintLogos.get(input.imprintId) ?? []).filter((logo) => !(logo.variant === input.variant && logo.format === input.format))
    logos.push({ variant: input.variant, format: input.format, storagePath: `${input.imprintId}/${input.variant.toLowerCase()}-${input.format.toLowerCase()}-${crypto.randomUUID()}.${input.format.toLowerCase()}`, url: URL.createObjectURL(input.file), originalFilename: input.file.name, mimeType: input.file.type, sizeBytes: input.file.size, createdAt: new Date().toISOString() })
    demoImprintLogos.set(input.imprintId, logos)
  }

  async deleteImprintLogo(input: ImprintLogoTarget) {
    await wait()
    const logos = demoImprintLogos.get(input.imprintId) ?? []
    if (!logos.some((logo) => logo.variant === input.variant && logo.format === input.format)) throw new DomainError("NOT_FOUND", "Logotipo não encontrado.")
    demoImprintLogos.set(input.imprintId, logos.filter((logo) => !(logo.variant === input.variant && logo.format === input.format)))
  }

  async deleteImprint(imprintId: string) {
    await wait()
    const index = demoTemplateCatalog.imprints.findIndex((candidate) => candidate.id === imprintId)
    if (index < 0) throw new DomainError("NOT_FOUND", "Selo não encontrado.")
    if (demoCatalogProducts.some((product) => product.imprint.id === imprintId)) {
      throw new DomainError("IMPRINT_HAS_PRODUCTS", "Este selo possui produtos associados e não pode ser apagado.")
    }
    if (demoTemplateCatalog.kitTemplates.some((template) => template.imprintId === imprintId)) {
      throw new DomainError("IMPRINT_HAS_KIT_TEMPLATES", "Este selo possui modelos de kit associados e não pode ser apagado.")
    }
    if (demoCatalogCollections.some((collection) => collection.imprint.id === imprintId)) {
      throw new DomainError("IMPRINT_HAS_COLLECTIONS", "Este selo possui coleções associadas e não pode ser apagado.")
    }
    demoTemplateCatalog.imprints.splice(index, 1)
    demoImprintLogos.delete(imprintId)
  }

  async listUsers(): Promise<WorkspaceUser[]> {
    await wait(60)
    return structuredClone(demoWorkspaceUsers).map((user) => ({ ...user, version: demoEditVersion(user.id) })).sort((left, right) => left.name.localeCompare(right.name, "pt-BR"))
  }

  async inviteUser(input: InviteUserInput) {
    await wait()
    const email = input.email.trim().toLowerCase()
    const name = input.name.trim()
    if (!name || !isBarcaWorkspaceEmail(email)) throw new DomainError("VALIDATION_ERROR", "Nome e e-mail @barca.cc são obrigatórios.")
    if (demoWorkspaceUsers.some((user) => user.email.toLowerCase() === email)) throw new DomainError("DUPLICATE_USER", "Já existe um usuário com este e-mail.")
    const id = `demo-user-${crypto.randomUUID()}`
    const now = new Date().toISOString()
    demoWorkspaceUsers.push({ id, name, email, role: input.role, functions: [], imprintIds: [], createdAt: now, updatedAt: now })
    return id
  }

  async setUserRole(userId: string, role: WorkspaceUser["role"]) {
    await wait()
    const user = demoWorkspaceUsers.find((candidate) => candidate.id === userId)
    if (!user) throw new DomainError("USER_NOT_FOUND", "Usuário não encontrado.")
    if (user.id === "demo-admin" && role !== "ADMIN") throw new DomainError("CANNOT_CHANGE_OWN_ROLE", "Você não pode alterar o próprio papel administrativo.")
    user.role = role
    user.updatedAt = new Date().toISOString()
    bumpDemoEditVersion(userId)
  }

  async setUserTeam(input: SetUserTeamInput) {
    await wait()
    const user = demoWorkspaceUsers.find((candidate) => candidate.id === input.userId)
    if (!user) throw new DomainError("USER_NOT_FOUND", "Usuário não encontrado.")
    if (input.expectedVersion !== undefined && input.expectedVersion !== demoEditVersion(user.id)) throw new DomainError("CONCURRENCY_CONFLICT", "O usuário foi alterado por outra pessoa.")
    const imprintIds = [...new Set(input.imprintIds)]
    if (imprintIds.some((imprintId) => !demoTemplateCatalog.imprints.some((imprint) => imprint.id === imprintId))) throw new DomainError("INVALID_IMPRINT", "Selecione selos válidos.")
    const functions = [...new Set(input.functions)]
    if (functions.includes("EDITOR_IN_CHIEF")) {
      const conflict = demoWorkspaceUsers.find((candidate) => candidate.id !== user.id && candidate.functions.includes("EDITOR_IN_CHIEF") && candidate.imprintIds.some((imprintId) => imprintIds.includes(imprintId)))
      if (conflict) {
        const imprint = demoTemplateCatalog.imprints.find((candidate) => conflict.imprintIds.includes(candidate.id) && imprintIds.includes(candidate.id))
        throw new DomainError("EDITOR_IN_CHIEF_CONFLICT", "Cada selo tem apenas um editor-chefe.", { parsed: { imprint: imprint?.name, user: conflict.name } })
      }
    }
    user.functions = functions
    user.imprintIds = imprintIds
    user.updatedAt = new Date().toISOString()
    bumpDemoEditVersion(user.id)
  }

  async updateUser(input: UpdateUserInput) {
    const user = demoWorkspaceUsers.find((candidate) => candidate.id === input.userId)
    if (!user) throw new DomainError("USER_NOT_FOUND", "Usuário não encontrado.")
    const name = input.name.trim()
    if (!name) throw new DomainError("VALIDATION_ERROR", "Nome é obrigatório.")
    await this.setUserTeam(input)
    user.name = name
    user.updatedAt = new Date().toISOString()
  }

  async deleteUser(userId: string) {
    await wait()
    const index = demoWorkspaceUsers.findIndex((candidate) => candidate.id === userId)
    if (index < 0) throw new DomainError("USER_NOT_FOUND", "Usuário não encontrado.")
    if (userId === "demo-admin") throw new DomainError("CANNOT_DELETE_OWN_USER", "Você não pode excluir a própria conta.")
    demoWorkspaceUsers.splice(index, 1)
  }

  async listPrintShops(): Promise<PrintShopSummary[]> {
    await wait(60)
    return structuredClone(demoPrintShops.map((shop) => ({
      ...shop,
      version: demoEditVersion(shop.id),
      createdAt: "2026-06-01T12:00:00.000Z",
      updatedAt: "2026-06-01T12:00:00.000Z",
      itemCount: new Set([
        ...demoCatalogProducts.flatMap((product) => product.items.filter((item) => item.printShop?.id === shop.id).map((item) => item.id)),
        ...demoProductionRuns.flatMap((run) => run.items.filter((item) => item.printShop?.id === shop.id).map((item) => item.catalogItemId ?? item.id)),
        ...demoProducts.flatMap((product) => product.items.filter((item) => item.printShop?.id === shop.id).map((item) => item.id)),
      ]).size,
      productionItems: [...demoProductionRuns.flatMap((run) => run.items
        .filter((item) => item.printShop?.id === shop.id)
        .map((item) => ({
          itemId: item.id,
          itemName: item.name,
          itemType: item.itemType.name,
          runId: run.id,
          productId: run.product.id,
          productCode: run.product.sku || "SKU PENDENTE",
          productName: run.product.name,
          productStatus: run.status,
          currentStageName: item.stageStates.find((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED")?.stageName ?? "Concluído",
          itemStatus: item.status,
          enteredAt: item.stageStates.find((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED")?.enteredAt ?? run.startedAt,
        }))), ...demoProducts.flatMap((product) => product.items.filter((item) => item.printShop?.id === shop.id).map((item) => ({
          itemId: item.id, itemName: item.name, itemType: item.typeLabel, productId: product.id,
          productCode: product.code, productName: product.name, productStatus: product.status,
          currentStageName: product.stages.find((stage) => stage.id === item.currentStageId)?.name ?? "Aguardando início",
          itemStatus: item.status, enteredAt: item.enteredAt,
        })))],
    })).sort((left, right) => left.name.localeCompare(right.name, "pt-BR")))
  }

  async createPrintShop(input: PrintShopInput) {
    await wait()
    const normalized = validatePrintShop(input)
    if (demoPrintShops.some((shop) => shop.name.toLocaleLowerCase("pt-BR") === normalized.name.toLocaleLowerCase("pt-BR"))) {
      throw new DomainError("DUPLICATE_PRINT_SHOP", "Já existe uma gráfica com este nome.")
    }
    const id = `print-shop-${crypto.randomUUID()}`
    demoPrintShops.push({ id, ...normalized })
    return id
  }

  async updatePrintShop(printShopId: string, input: PrintShopInput, expectedVersion?: number) {
    await wait()
    const shop = demoPrintShops.find((candidate) => candidate.id === printShopId)
    if (!shop) throw new DomainError("NOT_FOUND", "Gráfica não encontrada.")
    if (expectedVersion !== undefined && expectedVersion !== demoEditVersion(printShopId)) throw new DomainError("CONCURRENCY_CONFLICT", "A gráfica foi alterada por outra pessoa.")
    const normalized = validatePrintShop({ ...shop, ...input })
    if (demoPrintShops.some((candidate) => candidate.id !== printShopId && candidate.name.toLocaleLowerCase("pt-BR") === normalized.name.toLocaleLowerCase("pt-BR"))) {
      throw new DomainError("DUPLICATE_PRINT_SHOP", "Já existe uma gráfica com este nome.")
    }
    Object.assign(shop, normalized)
    for (const item of demoProducts.flatMap((product) => product.items)) {
      if (item.printShop?.id === printShopId) item.printShop = structuredClone(shop)
    }
    bumpDemoEditVersion(printShopId)
  }

  async deletePrintShop(printShopId: string) {
    await wait()
    const index = demoPrintShops.findIndex((candidate) => candidate.id === printShopId)
    if (index < 0) throw new DomainError("PRINT_SHOP_NOT_FOUND", "Gráfica não encontrada.")
    const inUse = demoCatalogProducts.some((product) => product.items.some((item) => item.printShop?.id === printShopId))
      || demoProductionRuns.some((run) => run.items.some((item) => item.printShop?.id === printShopId))
      || demoProducts.some((product) => product.items.some((item) => item.printShop?.id === printShopId))
    if (inUse) throw new DomainError("PRINT_SHOP_IN_USE", "Esta gráfica possui itens associados e não pode ser apagada.")
    demoPrintShops.splice(index, 1)
  }

  // O redirecionamento ao Google recarrega a página; o estado simulado sobrevive na sessão.
  private get driveConnection(): GoogleDriveConnection {
    try {
      const stored = window.sessionStorage.getItem(demoDriveConnectionKey)
      if (stored) return JSON.parse(stored) as GoogleDriveConnection
    } catch { /* armazenamento indisponível: cai no estado desconectado */ }
    return { status: "DISCONNECTED" }
  }

  private set driveConnection(value: GoogleDriveConnection) {
    try { window.sessionStorage.setItem(demoDriveConnectionKey, JSON.stringify(value)) } catch { /* sem persistência */ }
  }

  async getGoogleDriveConnection(): Promise<GoogleDriveConnection> {
    await wait()
    return this.driveConnection
  }

  async startGoogleDriveConnection(): Promise<string> {
    await wait()
    this.driveConnection = { status: "CONNECTED", accountEmail: "fturcheti@barca.cc", connectedAt: new Date().toISOString(), canUpload: true }
    return `${window.location.origin}/configuracoes/google-drive?drive=connected`
  }

  async disconnectGoogleDrive() {
    await wait()
    this.driveConnection = { status: "DISCONNECTED" }
  }

  // Demonstração: presume um arquivo na etapa atual de cada livro com pasta cadastrada.
  async syncProductionWithDrive(input: SyncProductionWithDriveInput): Promise<DriveSyncResult> {
    await wait()
    if (this.driveConnection.status !== "CONNECTED") throw new DomainError("DRIVE_NOT_CONNECTED", "Conecte a conta institucional do Google Drive em Configurações.")
    const run = findProductionRun(input.runId)
    assertActiveDemoRun(run)
    const items: DriveSyncItem[] = []
    for (const item of run.items.filter((candidate) => candidate.status !== "CANCELLED")) {
      const folderUrl = item.catalogSnapshot?.book?.editorialFilesUrl?.trim() || null
      const base = { itemId: item.id, name: item.name, folderUrl, folders: [], steps: [], warnings: [] }
      if (!item.itemType.isBook) { items.push({ ...base, status: "NOT_APPLICABLE" }); continue }
      if (!folderUrl) { items.push({ ...base, status: "NO_FOLDER" }); continue }
      const current = item.stageStates.find((state) => state.status === "IN_PROGRESS")
      if (!current) { items.push({ ...base, status: "READY" }); continue }
      const next = item.stageStates.find((state) => state.stagePosition > current.stagePosition)
      const step: DriveSyncItem["steps"][number] = { position: current.stagePosition, stageName: current.stageName, action: "DONE", applied: false }
      if (input.apply) {
        await this.transitionProductionItem({ runId: run.id, itemId: item.id, targetStageId: (next ?? current).stageId, expectedVersion: item.version, reason: "Sincronização com o Google Drive" })
        step.applied = true
      }
      items.push({ ...base, status: "READY", steps: [step] })
    }
    return { runId: run.id, applied: input.apply, skipEmptyStages: input.skipEmptyStages, items }
  }

  // Demonstração: sem acesso ao Drive, qualquer link de arquivo devolve a extração de uma ficha de exemplo.
  async extractCatalogRecordFromDrive(fileUrl: string): Promise<CatalogRecordExtraction> {
    await wait()
    if (!extractDriveFileId(fileUrl)) throw new DomainError("INVALID_FILE_URL", "O link não é de um arquivo do Google Drive.")
    if (this.driveConnection.status !== "CONNECTED") throw new DomainError("DRIVE_NOT_CONNECTED", "Conecte a conta institucional do Google Drive em Configurações.")
    const record = parseCatalogRecordText(demoCatalogRecordFileText)
    return { fileName: "Ficha catalográfica (demonstração).docx", record, fields: extractedCatalogRecordFields(record) }
  }

  // Demonstração: simula o envio para "Arquivos complementares" na pasta do item, sem tocar no Drive.
  async uploadSupplementaryFile(input: UploadSupplementaryFileInput): Promise<DriveUploadedFile> {
    if (!hasSupplementaryFileExtension(input.kind, input.file.name)) throw new DomainError("UNSUPPORTED_FILE", input.kind === "BARCODE" ? "O código de barras deve ser um arquivo SVG." : "Envie o arquivo do Word (.docx).")
    if (this.driveConnection.status !== "CONNECTED") throw new DomainError("DRIVE_NOT_CONNECTED", "Conecte a conta institucional do Google Drive em Configurações.")
    const item = (await this.getCatalogItem(input.itemId))
    if (!item.book?.editorialFilesUrl?.trim()) throw new DomainError("NO_FOLDER", "Salve o link da pasta do item no Google Drive antes de enviar o arquivo.")
    for (const fraction of [0.35, 0.7, 1]) {
      await wait()
      input.onProgress?.(fraction)
    }
    const savedFileUrl = supplementaryFileUrl(item, input.kind)
    const existingId = savedFileUrl ? extractDriveFileId(savedFileUrl) : null
    const id = existingId ?? `demo-${input.kind.toLowerCase()}-${Date.now().toString(36)}`
    return { id, name: supplementaryFileName(input.kind, item.name), replaced: Boolean(existingId), webViewLink: input.kind === "BARCODE" ? `https://drive.google.com/file/d/${id}/view` : `https://docs.google.com/document/d/${id}/edit`, folderName: SUPPLEMENTARY_FILES_FOLDER_NAME, folderUrl: "https://drive.google.com/drive/folders/demo-arquivos-complementares" }
  }

  // Demonstração: não há Drive; o arquivo salvo no item é dado como apagado.
  async trashSupplementaryFile(itemId: string, kind: SupplementaryFileKind) {
    await wait()
    const item = await this.getCatalogItem(itemId)
    return { trashed: Boolean(supplementaryFileUrl(item, kind)) }
  }

  // Demonstração: sem acesso ao Drive, qualquer link de arquivo devolve os créditos de uma ficha de exemplo.
  async extractCreditsFromDrive(fileUrl: string): Promise<CreditsExtraction> {
    await wait()
    if (!extractDriveFileId(fileUrl)) throw new DomainError("INVALID_FILE_URL", "O link não é de um arquivo do Google Drive.")
    return { fileName: "Ficha de créditos (demonstração).docx", credits: parseCreditsParagraphs(demoCreditsFileParagraphs) }
  }

  // Demonstração: sem acesso ao Drive, qualquer link de arquivo devolve a leitura de uma FAO de exemplo.
  async extractFaoFromDrive(fileUrl: string): Promise<FaoExtraction> {
    await wait()
    if (!extractDriveFileId(fileUrl)) throw new DomainError("INVALID_FILE_URL", "O link não é de um arquivo do Google Drive.")
    const fao = parseFaoParagraphs(demoFaoFileParagraphs)
    return { fileName: "FAO (demonstração).docx", fao, fields: extractedFaoFields(fao) }
  }

  // Demonstração: gera pastas plausíveis para cada etapa do item a partir do próprio pipeline.
  async getDriveItemStageFolders({ runId, itemId }: { runId: string; itemId: string }): Promise<DriveItemStageFolders> {
    await wait()
    if (this.driveConnection.status !== "CONNECTED") throw new DomainError("DRIVE_NOT_CONNECTED", "Conecte a conta institucional do Google Drive em Configurações.")
    const run = findProductionRun(runId)
    const item = run.items.find((candidate) => candidate.id === itemId)
    if (!item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    if (!item.itemType.isBook) throw new DomainError("NOT_APPLICABLE", "Este item não é um livro; não possui pastas de etapa no Drive.")
    const folderUrl = item.catalogSnapshot?.book?.editorialFilesUrl?.trim() || undefined
    if (!folderUrl) throw new DomainError("NO_FOLDER", "Sem pasta do Google Drive cadastrada no catálogo.")
    const stages: DriveStageFolder[] = item.stageStates.filter((state) => !state.stageUsesPreviousDriveFolder).sort((first, second) => first.stagePosition - second.stagePosition).map((state) => ({
      stageId: state.stageId,
      position: state.stagePosition,
      folderId: `demo-folder-${item.id}-${state.stageId}`,
      folderName: `${String(state.stagePosition).padStart(2, "0")}. ${state.stageName.toUpperCase()}`,
      folderUrl: `${folderUrl}/etapa-${state.stagePosition}`,
    }))
    return { itemId: item.id, folderUrl, stages }
  }

  async createProductDriveFolders(input: CreateProductDriveFoldersInput): Promise<ProductDriveFoldersResult> {
    await wait()
    if (this.driveConnection.status !== "CONNECTED") throw new DomainError("DRIVE_NOT_CONNECTED", "Conecte a conta institucional do Google Drive em Configurações.")
    if (!this.driveConnection.canUpload) throw new DomainError("DRIVE_WRITE_ACCESS_REQUIRED", "Reconecte o Google Drive em Configurações para permitir envios.")
    const run = findProductionRun(input.runId)
    if (run.status !== "ACTIVE" && run.status !== "STUCK") throw new DomainError("OPEN_PRODUCTION_REQUIRED", "Somente produções ativas ou travadas criam pastas no Drive.")
    if (!input.itemIds.length || new Set(input.itemIds).size !== input.itemIds.length) throw new DomainError("VALIDATION_ERROR", "Selecione itens distintos da produção.")
    const items = input.itemIds.map((itemId) => {
      const item = run.items.find((candidate) => candidate.id === itemId)
      if (!item) throw new DomainError("NOT_FOUND", "Um ou mais itens não pertencem a esta produção.")
      if (item.status === "CANCELLED") throw new DomainError("ITEM_CANCELLED", "Não é possível criar pastas para itens cancelados.")
      return item
    })
    const product = findCatalogProduct(run.product.id)
    const root = demoTemplateCatalog.imprints.find((imprint) => imprint.id === product.imprint.id)?.driveRootFolderUrl
    if (!root) throw new DomainError("IMPRINT_DRIVE_ROOT_REQUIRED", "Configure a pasta raiz de produção no Drive do selo em Configurações → Selos.")
    const scaffoldInput = {
      productFolderName: input.productFolderName,
      suffix: input.suffix,
      items: items.map((item) => ({ id: item.id, name: item.name, stages: item.stageStates.filter((state) => !state.stageUsesPreviousDriveFolder).map((state) => ({ id: state.stageId, position: state.stagePosition, shortName: state.stageShortName })) })),
    }
    const errors = validateScaffoldInput(scaffoldInput)
    if (errors.length) throw new DomainError("VALIDATION_ERROR", errors[0], { errors })
    const scaffold = buildProductFolderScaffold(scaffoldInput)
    let createdCount = 0
    let reusedCount = 0
    const findOrCreate = (parent: string, name: string) => {
      const key = `${parent}/${name}`
      let id = this.driveFolders.get(key)
      const created = !id
      if (!id) {
        id = `demo-${crypto.randomUUID()}`
        this.driveFolders.set(key, id)
      }
      if (created) createdCount += 1
      else reusedCount += 1
      return { id, name, url: `https://drive.google.com/drive/folders/${id}`, created }
    }
    const productFolder = findOrCreate(root, scaffold.productFolderName)
    const responseItems: ProductDriveFoldersResult["items"] = scaffold.items.map((scaffoldItem, index) => {
      const source = items[index]
      const folder = findOrCreate(productFolder.id, scaffoldItem.folderName)
      const stages = scaffoldItem.stages.map((stage) => {
        const created = findOrCreate(folder.id, stage.folderName)
        return { stageId: stage.id, name: created.name, created: created.created }
      })
      let linkedToCatalog = false
      let replacedExistingFolder = false
      if (source.catalogItemId && source.itemType.isBook) {
        const catalogItem = product.items.find((item) => item.id === source.catalogItemId)
        if (catalogItem?.book) {
          const previousUrl = catalogItem.book.editorialFilesUrl?.trim()
          if (previousUrl !== folder.url) {
            catalogItem.book.editorialFilesUrl = folder.url
            linkedToCatalog = true
            replacedExistingFolder = Boolean(previousUrl)
          }
        }
      }
      return { itemId: source.id, name: folder.name, folderUrl: folder.url, created: folder.created, linkedToCatalog, replacedExistingFolder, stages }
    })
    product.driveFolderUrl = productFolder.url
    product.version += 1
    product.updatedAt = new Date().toISOString()
    return { productFolder, items: responseItems, createdCount, reusedCount }
  }

  // Demonstração: simula o upload direto ao Google Drive com alguns avanços de progresso.
  async uploadFileToDriveStage(input: UploadToDriveStageInput): Promise<DriveUploadedFile> {
    if (this.driveConnection.status !== "CONNECTED") throw new DomainError("DRIVE_NOT_CONNECTED", "Conecte a conta institucional do Google Drive em Configurações.")
    if (!this.driveConnection.canUpload) throw new DomainError("DRIVE_WRITE_ACCESS_REQUIRED", "Reconecte o Google Drive em Configurações para habilitar envios.")
    const run = findProductionRun(input.runId)
    assertActiveDemoRun(run)
    const item = run.items.find((candidate) => candidate.id === input.itemId)
    if (!item) throw new DomainError("NOT_FOUND", "Item não encontrado.")
    const current = item.stageStates.find((state) => state.status === "IN_PROGRESS" || state.status === "BLOCKED")
    if (!current || current.stageId !== input.stageId) throw new DomainError("STAGE_NOT_CURRENT", "Esta não é a etapa atual do item.")
    const folders = await this.getDriveItemStageFolders({ runId: input.runId, itemId: input.itemId })
    const folder = folders.stages.find((candidate) => candidate.stageId === input.stageId)
    if (!folder) throw new DomainError("STAGE_FOLDER_NOT_FOUND", "Pasta da etapa não encontrada no Drive.")
    for (const fraction of [0.25, 0.5, 0.75, 1]) {
      await wait(120)
      input.onProgress?.(fraction)
    }
    return { id: `demo-file-${Date.now()}`, name: input.file.name, webViewLink: folder.folderUrl, folderName: folder.folderName, folderUrl: folder.folderUrl }
  }
}

const demoDriveConnectionKey = "farol-demo-google-drive-connection"

function validatePrintShop(input: PrintShopInput) {
  const result = printShopSchema.safeParse(printShopToInput(input))
  if (!result.success) {
    throw new DomainError("VALIDATION_ERROR", result.error.issues[0]?.message ?? "Confira os dados da gráfica.")
  }
  return { ...result.data, contact: "" }
}

function supplementaryFileUrl(item: CatalogItem, kind: SupplementaryFileKind) {
  if (kind === "CREDITS") return item.book?.creditsFileUrl
  if (kind === "FAO") return item.book?.fao?.driveFileUrl
  if (kind === "BARCODE") return item.book?.barcodeFileUrl
  return item.book?.catalogRecord?.driveFileUrl
}
